/**
 * One place that turns a view and a time window into the numbers on screen.
 *
 * Every tab reads its figures from here. Nothing else adds up a daily series
 * for a tile, so one number is worked out one way on every tab.
 *
 * The rules a selector keeps:
 *
 *   1. A figure says what period it covers (`scope`). `window` means it
 *      follows the time control. When a snapshot does not carry the daily data
 *      a figure needs, the selector gives the lifetime number tagged
 *      `all-time`, never a lifetime number passed off as the window's.
 *   2. Zero is a number. A window with nothing in it is 0, never a reason to
 *      show the lifetime figure.
 *   3. When the data is not loaded yet, the answer is `{ needs: [...] }`: the
 *      tiers to ask the fleet for. A selector never guesses.
 *   4. A figure that is close but not exact says so (`approx`) and says why
 *      in one sentence (`note`).
 *
 * Selectors are plain functions of their arguments. Wrap a call in `useMemo`.
 */
import type { Scope } from './ui/kit';
import type { FleetTier, Freshness } from './fleetData';
import type {
  AgentRow,
  ClientSnapshot,
  DashboardView,
  KnowledgeBaseRow,
  ScheduleRow,
  TopConversation,
  UserAppRow,
  UserAppsBlock,
  UserAppsInventory,
  UserAppsTotals,
} from '@/types/fleetAnalytics';
import {
  activePeopleInWindow,
  addDays,
  ageHours,
  agentKey,
  aggregateSnapshots,
  asOfDay,
  buildDayRange,
  dailyActivePeople,
  peopleActivityMap,
  SCHEDULE_COUNTS_AS_ACTIVE,
  ceForSnapshot,
  costOwnerPerAccount,
  detectOutliers,
  friendlyAccountName,
  infraDisplayName,
  isAggregate,
  isInfraAccount,
  isModelLineService,
  isStaleAt,
  modelsInWindow,
  nzBillingMonth,
  overAllocation,
  productOf,
  snapshotForDisplay,
  stackKind,
  stackLabel,
  sumByKeyInWindow,
  sumDailyInWindow,
  tiersNeededAcross,
  tiersNeededFor,
  topConvInWindow,
  topConvSpanSeconds,
  trailingRatio,
} from './shared';
import type { StackKind, WindowState } from './shared';
import { computeInferredCosts, INFERRED_LLM_SERVICE_LABEL, spendChatDaily } from './inferredCosts';
import { notBilledReason, snapshotContract } from './contracts';
import type { ContractOf, StackContract } from './contracts';
import type { QuotaSharingRole } from './inferredCosts';
import { computeClientUserMap } from './clientUserMap';
import type { UserEngagementBucket, UserMapMetrics, UserMapStats } from './clientUserMap';

// ─── the shapes every selector answers in ────────────────────────────────

/** One number and the period it covers. */
export interface Figure<T = number> {
  value: T;
  /** `window` follows the time control. Anything else must be tagged on screen. */
  scope: Scope;
  /** True when the number is close but not exact. `note` says why. */
  approx?: boolean;
  /** One plain sentence about the number: why it is approximate, or what it leaves out. */
  note?: string;
}

/** The data is not loaded yet. Ask the fleet for these tiers. */
export interface Needs {
  needs: FleetTier[];
}

/** An answer, or the tiers that must load before there is one. */
export type Tiered<T> = T | Needs;

/** True when a selector gave an answer, false when it asked for data. */
export function isReady<T>(x: Tiered<T>): x is T {
  return !(typeof x === 'object' && x !== null && Array.isArray((x as Needs).needs));
}

/** Every tier a set of answers is still waiting for. Feed it to `fleet.ensureTier` or `<NeedsTier>`. */
export function needsOf(...answers: unknown[]): FleetTier[] {
  const out = new Set<FleetTier>();
  for (const a of answers) {
    if (typeof a !== 'object' || a === null) continue;
    const needs = (a as Partial<Needs>).needs;
    const pending = (a as { pending?: FleetTier[] }).pending;
    for (const t of Array.isArray(needs) ? needs : []) out.add(t);
    for (const t of Array.isArray(pending) ? pending : []) out.add(t);
  }
  return Array.from(out);
}

/**
 * The props of a `<Kpi>` or a `<Fact>` that come from one figure:
 * `<Kpi label="Conversations" {...kpiProps(chat.conversations, fmtN)} />`.
 * A figure still waiting for data reads "…"; one with no value reads "n/a".
 * An approximate figure shows its number as it is and sets `approx`, which the
 * kit draws as a small "approx." beside the name with `note` on hover. The
 * value itself never carries the word "about".
 */
export function kpiProps<T>(
  figure: Tiered<Figure<T>> | null | undefined,
  format: (value: NonNullable<T>) => string
): { value: string; scope?: Scope; approx?: boolean; note?: string } {
  if (!figure) return { value: 'n/a' };
  if (!isReady(figure)) return { value: '…' };
  if (figure.value === null || figure.value === undefined)
    return { value: 'n/a', scope: figure.scope, note: figure.note };
  return {
    value: format(figure.value as NonNullable<T>),
    scope: figure.scope,
    approx: figure.approx || undefined,
    note: figure.note,
  };
}

const fig = <T>(value: T, scope: Scope, extra?: { approx?: boolean; note?: string }): Figure<T> =>
  extra?.approx || extra?.note ? { value, scope, ...extra } : { value, scope };

const ratio = (n: number, d: number): number | null => (d > 0 ? n / d : null);

const nameList = (names: string[], max = 4): string =>
  names.length <= max ? names.join(', ') : `${names.slice(0, max).join(', ')} and ${names.length - max} more`;

/**
 * For a figure built from a newer daily series: the stacks in a summed view
 * that do not carry the series yet. They are left out of the figure, and the
 * note says which.
 */
function leftOut(data: DashboardView, path: string): { approx?: boolean; note?: string } {
  const names = data.portal?.missing?.[path] ?? [];
  if (!names.length) return {};
  const what = names.length === 1 ? '1 stack has' : `${names.length} stacks have`;
  return {
    approx: true,
    note: `${what} no daily record of this yet and ${names.length === 1 ? 'is' : 'are'} left out: ${nameList(names)}.`,
  };
}

/** The last day a view holds data for: the day of its (newest) nightly update. */
function lastDataDay(data: DashboardView): string {
  return asOfDay({ generated_at: isAggregate(data) ? data.newest_generated_at : data.generated_at });
}

/**
 * The days a chart draws: the window, stopped at the last day the data runs
 * to. The nightly update leaves today (and any later day of the window)
 * with nothing in it, and a line drawn through those days falls to zero as
 * if use had stopped. `map`, when given, can carry the days on: a series
 * kept by New Zealand day can hold a day the update's own UTC day has not
 * reached.
 */
function chartDays(data: DashboardView, win: WindowState, map?: Record<string, number>): string[] {
  let last = lastDataDay(data);
  for (const d of Object.keys(map || {})) if (d > last && d <= win.endDate) last = d;
  return buildDayRange(win.startDate, last < win.endDate ? last : win.endDate);
}

/** A note for a per-person, per-agent or per-service figure when the window reaches back past the data. */
function beforeDetail(data: { portal?: DashboardView['portal'] }, win: WindowState, what: string) {
  const from = data.portal?.detail_from;
  if (!from || win.startDate >= from) return {};
  return { approx: true, note: `${what} starts on ${from} in this data. Earlier days in the window are not counted.` };
}

// ─── spend ───────────────────────────────────────────────────────────────

export interface Spend {
  /**
   * What Numa cost to run: the AWS bill with its model lines taken out and
   * the token-priced chat cost put in. The headline spend number.
   */
  inferred: Figure;
  /** The AWS bill as billed to the account. For a standalone customer: the Numa part of it. */
  awsBill: Figure;
  /** The model lines on the AWS bill. */
  modelLines: Figure;
  /** Chat and agent model use, priced by Numa from token counts. */
  chat: Figure;
  /** Model use billed to this account for other clients (quota sharing). */
  lent: Figure;
  /** This selection's model use billed to another account. */
  borrowed: Figure;
  role: QuotaSharingRole;
  /**
   * Standalone customers: spend on the customer's own systems in the same AWS
   * account, left out of every figure above. Null when nothing is left out.
   */
  notNuma: Figure | null;
  /**
   * AWS bills about two days late. `days` is how many days at the end of the
   * window have no bill yet, and `usd` is the model usage on them. Those days
   * are in `inferred` as model usage alone (the rest of their bill is still
   * to come) and in neither `lent` nor `borrowed`. Null when every day of the
   * window is billed.
   */
  notBilledYet: { days: number; usd: number } | null;
  /**
   * Day by day, for a chart. The three arrays line up with `days`, which stop
   * at the last day the data runs to: do not draw past it.
   */
  daily: { days: string[]; inferred: number[]; awsBill: number[]; chat: number[] };
  /**
   * A summed view: the part of each figure above that belongs to the Inactive
   * stacks the view hides. Their money was spent, so it is in the figures; a
   * tile says how much of it is theirs. Null when the view hides none, or
   * when they spent nothing in the window.
   */
  inactive: InactiveSpend | null;
}

/** What the hidden Inactive stacks of a view spent in the window. */
export interface InactiveSpend {
  /** The hidden stacks, by name. */
  stacks: string[];
  /** Their part of total spend: the bill of the accounts only they hold, less its model lines, plus their model use. */
  spend: number;
  /** Their part of the AWS bill. */
  awsBill: number;
  /** Their model use, priced from tokens. */
  chat: number;
}

/** The spend in the window of the Inactive stacks a summed view hides, or null. */
export function selectInactiveSpend(data: DashboardView, win: WindowState): InactiveSpend | null {
  const hidden = isAggregate(data) ? data.inactive_spend : undefined;
  if (!hidden?.stacks.length) return null;
  const awsBill = sumDailyInWindow(hidden.bill_daily, win);
  const lines = sumDailyInWindow(hidden.model_lines_daily, win);
  const chat = sumDailyInWindow(hidden.chat_daily, win);
  if (awsBill === 0 && chat === 0) return null;
  return { stacks: hidden.stacks, spend: awsBill - lines + chat, awsBill, chat };
}

/**
 * Spend for the window. Needs the `services` tier only while a snapshot does
 * not carry its model lines ready-made (snapshots older than the tiered
 * index); the page asks for that tier itself, so a tab only has to handle
 * the short wait.
 */
export function selectSpend(data: DashboardView, win: WindowState): Tiered<Spend> {
  const inferred = computeInferredCosts(data, win);
  let quality: { approx?: boolean; note?: string } = {};
  if (!inferred.modelLinesKnown) {
    const need = tiersNeededFor(data, 'cost_explorer.by_service_daily');
    if (need.length) return { needs: need };
    const names = data.portal?.missing?.['cost_explorer.model_lines'] ?? [];
    quality = {
      approx: true,
      note: `The AWS bill of ${names.length || 1} ${names.length > 1 ? 'accounts has' : 'account has'} no breakdown by service, so model use there is counted twice.`,
    };
  } else if (inferred.modelLinesFrom) {
    quality = {
      approx: true,
      note: `Before ${inferred.modelLinesFrom} this data has no breakdown of the AWS bill, so model use before that day is counted twice.`,
    };
  }

  // Days with no bill yet hold model usage alone, so the total is short of
  // those days' other AWS cost. Said on the figure, with any note already there.
  let notBilledYet: Spend['notBilledYet'] = null;
  let inferredQuality = quality;
  if (inferred.totals.unbilled > 0) {
    notBilledYet = { days: inferred.unbilledDays, usd: inferred.totals.unbilled };
    const n = inferred.unbilledDays;
    const late =
      n > 0
        ? `The last ${n === 1 ? 'day is' : `${n} days are`} not billed by AWS yet: spend for ${n === 1 ? 'it' : 'them'} is model usage alone.`
        : 'AWS has not billed some accounts for their latest days yet: spend for those days is model usage alone.';
    inferredQuality = { approx: true, note: [quality.note, late].filter(Boolean).join(' ') };
  }

  const days = chartDays(data, win);
  const billByDay = data.cost_explorer?.totals_by_day || {};
  const notNumaDaily = data.cost_explorer?.non_numa_filtered_daily;
  const notNumaTotal = data.cost_explorer?.non_numa_filtered_total || 0;
  let notNuma: Figure | null = null;
  if (notNumaDaily) {
    const inWindow = sumDailyInWindow(notNumaDaily, win);
    if (inWindow > 0 || notNumaTotal > 0) notNuma = fig(inWindow, 'window');
  } else if (notNumaTotal > 0) {
    notNuma = fig(notNumaTotal, 'all-time');
  }

  return {
    inferred: fig(inferred.totals.inferred, 'window', inferredQuality),
    awsBill: fig(inferred.totals.ceTotal, 'window'),
    modelLines: fig(inferred.totals.ceClaude, 'window', quality),
    chat: fig(inferred.totals.chat, 'window'),
    lent: fig(inferred.totals.lent, 'window', quality),
    borrowed: fig(inferred.totals.borrowed, 'window', quality),
    role: inferred.role,
    notNuma,
    notBilledYet,
    daily: {
      days,
      inferred: days.map((d) => inferred.inferredTotalDaily[d] || 0),
      awsBill: days.map((d) => billByDay[d] || 0),
      chat: days.map((d) => inferred.chatDaily[d] || 0),
    },
    inactive: selectInactiveSpend(data, win),
  };
}

export interface ServiceMix {
  /** Largest first. Every model line of the bill is one row: `Model use, priced from tokens`. */
  rows: Array<{ service: string; usd: number; share: number; isModelUse: boolean }>;
  total: number;
  scope: Scope;
  approx?: boolean;
  note?: string;
}

/** Spend by AWS service for the window. Needs the `services` tier (a standalone customer has it at once). */
export function selectServiceMix(data: DashboardView, win: WindowState): Tiered<ServiceMix> {
  const ce = data.cost_explorer;
  const totals: Record<string, number> = {};
  let scope: Scope = 'window';
  let quality: { approx?: boolean; note?: string } = {};

  if (ce?.by_service_daily) {
    for (const [svc, days] of Object.entries(ce.by_service_daily)) {
      if (isModelLineService(svc)) continue;
      const sum = sumDailyInWindow(days, win);
      if (sum > 0) totals[svc] = sum;
    }
    const chat = sumDailyInWindow(spendChatDaily(data), win);
    if (chat > 0) totals[INFERRED_LLM_SERVICE_LABEL] = chat;
    quality = beforeDetail(data, win, 'The breakdown by service');
  } else {
    const need = tiersNeededFor(data, 'cost_explorer.by_service_daily');
    if (need.length) return { needs: need };
    // A snapshot with no daily breakdown at all: the lifetime split, as billed.
    scope = 'all-time';
    for (const [svc, v] of Object.entries(ce?.totals_by_service || {})) {
      if ((v || 0) > 0) totals[svc] = v;
    }
  }

  const total = Object.values(totals).reduce((a, b) => a + b, 0);
  const rows = Object.entries(totals)
    .sort((a, b) => b[1] - a[1])
    .map(([service, usd]) => ({
      service,
      usd,
      share: total > 0 ? usd / total : 0,
      isModelUse: service === INFERRED_LLM_SERVICE_LABEL,
    }));
  return { rows, total, scope, ...quality };
}

export interface UsageTypes {
  /** Largest first. */
  rows: Array<{ usageType: string; usd: number; share: number }>;
  total: number;
  scope: Scope;
}

/** Bedrock spend by usage type (input tokens, cache writes, ...). Needs the `services` tier. */
export function selectBedrockUsageTypes(data: DashboardView, win: WindowState): Tiered<UsageTypes> {
  const need = tiersNeededFor(data, 'cost_explorer.bedrock_usage_type_daily');
  if (need.length) return { needs: need };
  const daily = data.cost_explorer?.bedrock_usage_type_daily;
  const totals = daily ? sumByKeyInWindow(daily, win) : data.cost_explorer?.bedrock_usage_type_totals || {};
  const total = Object.values(totals).reduce((a, b) => a + (b || 0), 0);
  const rows = Object.entries(totals)
    .filter(([, usd]) => (usd || 0) > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([usageType, usd]) => ({ usageType, usd, share: total > 0 ? usd / total : 0 }));
  return { rows, total, scope: daily ? 'window' : 'all-time' };
}

// ─── chat ────────────────────────────────────────────────────────────────

const COUNTED_PER_DAY =
  'This counts a conversation once for each day it was active, so it reads a little high. The next nightly update counts each conversation once.';

/** The daily conversation series a view holds, and whether it is the true count. */
function conversationSeries(data: DashboardView): {
  map: Record<string, number> | undefined;
  quality: { approx?: boolean; note?: string };
} {
  const started = data.chat?.daily_convs_started;
  if (!started) return { map: data.chat?.daily_convs, quality: { approx: true, note: COUNTED_PER_DAY } };
  const behind = data.portal?.missing?.['chat.daily_convs_started'] ?? [];
  if (!behind.length) return { map: started, quality: {} };
  return {
    map: started,
    quality: {
      approx: true,
      note: `${behind.length === 1 ? '1 stack still counts' : `${behind.length} stacks still count`} a conversation once for each day it was active: ${nameList(behind)}.`,
    },
  };
}

export interface ChatFigures {
  /**
   * Chat and agent model use, priced from token counts, of the stacks whose
   * use the view counts. The unit costs divide this one.
   */
  cost: Figure;
  /**
   * Model spend: `cost` plus the model use of the Inactive stacks a summed
   * view hides. The same as `selectSpend(...).chat`, from core data alone.
   */
  modelSpend: Figure;
  /** Conversations started in the window. Approximate on snapshots that only count per active day. */
  conversations: Figure;
  /** Conversations a person started (plain chat and chat with an agent). */
  adhocConversations: Figure;
  /** Conversations a schedule or a trigger started. */
  scheduledConversations: Figure;
  /** Messages people sent. */
  messages: Figure;
  toolCalls: Figure;
  /** Model turns. */
  turns: Figure;
  /** Null when the window holds no conversations. */
  costPerConversation: Figure<number | null>;
  /** Null when the window holds no messages. */
  costPerMessage: Figure<number | null>;
}

/** The core chat numbers for the window. Core data only. */
export function selectChat(data: DashboardView, win: WindowState): ChatFigures {
  const chat = data.chat;
  const cost = sumDailyInWindow(chat?.daily_cost, win);
  const messages = sumDailyInWindow(chat?.daily_messages, win);
  const { map, quality } = conversationSeries(data);
  const conversations = sumDailyInWindow(map, win);

  let adhoc: number;
  let scheduled: number;
  const startedByCategory = chat?.daily_convs_started_by_category;
  if (startedByCategory) {
    scheduled = sumDailyInWindow(startedByCategory.agent_run, win);
    adhoc = sumDailyInWindow(startedByCategory.plain_chat, win) + sumDailyInWindow(startedByCategory.agent_chat, win);
  } else {
    scheduled = sumDailyInWindow(chat?.daily_scheduled_count, win);
    adhoc = sumDailyInWindow(chat?.daily_adhoc_count, win);
  }

  return {
    cost: fig(cost, 'window'),
    modelSpend: fig(sumDailyInWindow(spendChatDaily(data), win), 'window'),
    conversations: fig(conversations, 'window', quality),
    adhocConversations: fig(adhoc, 'window', quality),
    scheduledConversations: fig(scheduled, 'window', quality),
    messages: fig(messages, 'window'),
    toolCalls: fig(sumDailyInWindow(chat?.daily_tool_calls, win), 'window'),
    turns: fig(sumDailyInWindow(chat?.daily_turns, win), 'window'),
    costPerConversation: fig(ratio(cost, conversations), 'window', quality),
    costPerMessage: fig(ratio(cost, messages), 'window'),
  };
}

export type CategoryKey = 'plain_chat' | 'agent_chat' | 'agent_run';

/**
 * The three kinds of conversation. The third is conversations a schedule or
 * a trigger started. They are conversations, counted from chat traces: the
 * count of scheduled RUNS comes from run records (`selectScheduled`), and the
 * two are different numbers.
 */
export const CATEGORY_LABEL: Record<CategoryKey, string> = {
  plain_chat: 'Numa chat',
  agent_chat: 'Chat with an agent',
  agent_run: 'Started by a schedule',
};

export interface CategoryRow {
  key: CategoryKey;
  label: string;
  cost: number;
  messages: number;
  conversations: number;
  turns: number;
  toolCalls: number;
  /** Null when there are no messages (a scheduled run has none). */
  costPerMessage: number | null;
  costPerConversation: number | null;
  shareOfCost: number;
}

export interface Categories {
  /** Always three rows, in the order plain chat, chat with an agent, started by a schedule. */
  rows: CategoryRow[];
  /** One scope for the whole table: `all-time` only when the snapshot has no daily split by kind. */
  scope: Scope;
  /** True when conversations are counted once per active day. */
  conversationsApprox: boolean;
  note?: string;
}

/**
 * Cost and volume by kind of conversation. A kind with nothing in the window
 * reads 0: the lifetime figure is used only when the snapshot carries no
 * daily split at all, and the whole table is then tagged `all-time`.
 */
export function selectCategories(data: DashboardView, win: WindowState): Categories {
  const chat = data.chat;
  const keys: CategoryKey[] = ['plain_chat', 'agent_chat', 'agent_run'];
  const hasDaily = chat?.daily_cost_by_category !== undefined;
  const { quality } = conversationSeries(data);
  const convMap = chat?.daily_convs_started_by_category ?? chat?.daily_convs_by_category;

  const raw = keys.map((key) => {
    if (!hasDaily) {
      const all = chat?.by_category?.[key];
      return {
        key,
        cost: all?.cost || 0,
        messages: all?.user_messages || 0,
        conversations: all?.convs || 0,
        turns: all?.turns || 0,
        toolCalls: all?.tool_calls || 0,
      };
    }
    return {
      key,
      cost: sumDailyInWindow(chat?.daily_cost_by_category?.[key], win),
      messages: sumDailyInWindow(chat?.daily_messages_by_category?.[key], win),
      conversations: sumDailyInWindow(convMap?.[key], win),
      turns: sumDailyInWindow(chat?.daily_turns_by_category?.[key], win),
      toolCalls: sumDailyInWindow(chat?.daily_tool_calls_by_category?.[key], win),
    };
  });
  const totalCost = raw.reduce((a, r) => a + r.cost, 0);
  return {
    rows: raw.map((r) => ({
      ...r,
      label: CATEGORY_LABEL[r.key],
      costPerMessage: ratio(r.cost, r.messages),
      costPerConversation: ratio(r.cost, r.conversations),
      shareOfCost: totalCost > 0 ? r.cost / totalCost : 0,
    })),
    scope: hasDaily ? 'window' : 'all-time',
    conversationsApprox: hasDaily ? Boolean(quality.approx) : false,
    note: hasDaily ? quality.note : undefined,
  };
}

/**
 * How long a conversation lasts on average, first request to last, in
 * seconds. Follows the window on a snapshot with the daily length record.
 * Without it the answer is the lifetime average, worked out from the
 * per-person rows, so it needs the `users` tier.
 */
export function selectAvgConversationLength(data: DashboardView, win: WindowState): Tiered<Figure<number | null>> {
  const sums = data.chat?.daily_span_seconds_sum;
  const counts = data.chat?.daily_span_count;
  if (sums && counts) {
    return fig(
      ratio(sumDailyInWindow(sums, win), sumDailyInWindow(counts, win)),
      'window',
      leftOut(data, 'chat.daily_span_seconds_sum')
    );
  }
  const need = tiersNeededFor(data, 'chat.by_user');
  if (need.length) return { needs: need };
  let span = 0;
  let convs = 0;
  for (const u of Object.values(data.chat?.by_user || {})) {
    if (u?.avg_span_seconds == null || !u.convs) continue;
    span += u.avg_span_seconds * u.convs;
    convs += u.convs;
  }
  return fig(ratio(span, convs), 'all-time');
}

// ─── people ──────────────────────────────────────────────────────────────

/**
 * People who sent a message inside the window, each counted once. Needs the
 * `users` tier (or a single stack's full snapshot): the count comes from the
 * per-person daily record, and nothing in the core data can stand in for it.
 */
export function selectActiveUsers(data: DashboardView, win: WindowState): Tiered<Figure> {
  const need = tiersNeededFor(data, 'chat.daily_adhoc_messages_by_user', 'chat.daily_messages_by_user');
  if (need.length) return { needs: need };
  const { map, exact } = peopleActivityMap(data.chat);
  if (!map) return fig(data.chat?.totals?.users || 0, 'all-time');
  // "Active" means the person used Numa themselves. An older snapshot cannot
  // tell a typed message from a schedule's prompt, and the figure says so.
  const mixed = data.portal?.missing?.['chat.daily_adhoc_active_users'] ?? [];
  const quality = beforeDetail(data, win, 'The per-person record');
  if (!exact || mixed.length) {
    return fig(activePeopleInWindow(map, win), 'window', {
      approx: true,
      note: [SCHEDULE_COUNTS_AS_ACTIVE, quality.note].filter(Boolean).join(' '),
    });
  }
  return fig(activePeopleInWindow(map, win), 'window', quality);
}

/** People with a login, as it stands now. */
export function selectProvisionedUsers(data: DashboardView): Figure {
  return fig(data.users?.provisioned_users || 0, 'now');
}

/** The most people active on any one day of the window. Core data only: a floor for "active people". */
export function selectPeakDailyUsers(data: DashboardView, win: WindowState): Figure {
  const { map, exact } = dailyActivePeople(data.chat);
  let peak = 0;
  for (const [d, v] of Object.entries(map || {})) {
    if (d >= win.startDate && d <= win.endDate && v > peak) peak = v;
  }
  return fig(peak, 'window', exact ? undefined : { approx: true, note: SCHEDULE_COUNTS_AS_ACTIVE });
}

// ─── per active person ───────────────────────────────────────────────────

export interface PerActivePerson {
  /** Messages in the window for each person active in it. */
  messages: Tiered<Figure<number | null>>;
  /** Chat and agent model cost in the window for each person active in it. */
  cost: Tiered<Figure<number | null>>;
}

/**
 * Messages and model cost for each active person. The one definition of
 * "per active person" on the dashboard: the window's total divided by the
 * people who sent a message in the window. Needs what `selectActiveUsers`
 * needs. A value is null when nobody was active, and when the data counts
 * people over all time only (a lifetime head count cannot divide one
 * window's total).
 */
export function selectPerActivePerson(data: DashboardView, win: WindowState): PerActivePerson {
  const active = selectActiveUsers(data, win);
  if (!isReady(active)) return { messages: active, cost: active };
  const chat = selectChat(data, win);
  const per = (total: Figure, what: string): Figure<number | null> => {
    if (active.scope !== 'window') {
      return fig<number | null>(null, 'window', {
        note: 'This data does not record who was active by day, so there is no figure for the window.',
      });
    }
    return fig<number | null>(ratio(total.value, active.value), 'window', {
      approx: active.approx,
      note: active.note ?? `${what} in the window, divided by the people active in it.`,
    });
  };
  return { messages: per(chat.messages, 'Messages'), cost: per(chat.cost, 'Chat and agent model cost') };
}

export interface Engagement {
  /** The day the 30 days run up to. */
  asOf: string;
  /** Everyone on the map: people with a login, and anyone with a message on record. */
  people: number;
  /** How many people are in each band. Scope `last-30d`: the 30 days up to `asOf`. */
  bands: Record<UserEngagementBucket, Figure>;
  /** One entry per person, for the map and its detail panel. */
  metrics: UserMapMetrics[];
  stats: UserMapStats;
  /**
   * True on an older snapshot, which cannot tell a message a person typed
   * from a prompt one of their schedules sent: a person whose schedule ran is
   * then on the map as active. `note` says so in a sentence.
   */
  countsSchedules: boolean;
  note?: string;
}

/**
 * The people of one stack by how many of the 30 days up to `asOf` they were
 * active: Power (15 or more), Engaged (6 to 14), Casual (1 to 5), Dormant
 * (none). Pass `selection.asOf`, the stack's own snapshot day: measured from
 * today's date, a stack whose nightly update is behind reads as a stack
 * nobody uses. Needs the per-person data.
 */
export function selectEngagement(stack: ClientSnapshot, asOf: string): Tiered<Engagement> {
  const need = tiersNeededFor(
    stack,
    'chat.by_user',
    'chat.daily_adhoc_messages_by_user',
    'chat.daily_messages_by_user',
    'users.sub_to_email'
  );
  if (need.length) return { needs: need };
  const map = computeClientUserMap(stack, asOf);
  const counts: Record<UserEngagementBucket, number> = { power: 0, engaged: 0, casual: 0, dormant: 0 };
  for (const m of map.metrics) counts[m.engagementBucket] += 1;
  const band = (key: UserEngagementBucket): Figure => fig(counts[key], 'last-30d');
  return {
    asOf: map.asOf,
    people: map.metrics.length,
    bands: { power: band('power'), engaged: band('engaged'), casual: band('casual'), dormant: band('dormant') },
    metrics: map.metrics,
    stats: map.stats,
    countsSchedules: map.countsSchedules,
    note: map.countsSchedules ? SCHEDULE_COUNTS_AS_ACTIVE : undefined,
  };
}

// ─── unit costs ──────────────────────────────────────────────────────────

export interface UnitCosts {
  /** Chat cost over conversations. Approximate on snapshots that count a conversation once per active day. */
  perConversation: Figure<number | null>;
  /** Chat cost over messages people sent. */
  perMessage: Figure<number | null>;
  /** Model cost of scheduled and triggered runs over the runs, both counted from run records. */
  perScheduledRun: Figure<number | null>;
  /** Chat cost over people active in the window: `selectPerActivePerson(...).cost`. Needs the `users` tier. */
  perActivePerson: Tiered<Figure<number | null>>;
  /** Messages over people active in the window: `selectPerActivePerson(...).messages`. Needs the `users` tier. */
  messagesPerActivePerson: Tiered<Figure<number | null>>;
}

/** The cost of one of each thing, for the window. A value is null when there were none of the thing. */
export function selectUnitCosts(data: DashboardView, win: WindowState): UnitCosts {
  const chat = selectChat(data, win);
  const scheduled = selectScheduled(data, win);
  const perPerson = selectPerActivePerson(data, win);
  return {
    perConversation: chat.costPerConversation,
    perMessage: chat.costPerMessage,
    perScheduledRun: fig<number | null>(ratio(scheduled.cost.value, scheduled.runs.value), 'window'),
    perActivePerson: perPerson.cost,
    messagesPerActivePerson: perPerson.messages,
  };
}

/** Model spend as a share of total spend, 0 to 1. Null when there is no spend. Takes the answer of `selectSpend`. */
export function selectModelShare(spend: Spend): Figure<number | null> {
  return fig<number | null>(ratio(spend.chat.value, spend.inferred.value), spend.inferred.scope, {
    approx: spend.inferred.approx,
    note: spend.inferred.note,
  });
}

export type UnitCostMetric = 'perMessage' | 'perConversation' | 'perActivePersonDay';

export interface UnitCostSeries {
  /** Every day of the window, oldest first. */
  days: string[];
  /** The day's own cost over the day's own count. Null on a day with none of the thing: a gap, never a zero. */
  daily: Array<number | null>;
  /**
   * For each day, cost over count for the `trailingDays` up to and including
   * it: a true trailing average. The first days of the window reach back
   * before the window for their history, so the line does not start short.
   */
  trailing: Array<number | null>;
  trailingDays: number;
  /** False when the data does not carry one of the two daily records. */
  available: boolean;
  approx?: boolean;
  note?: string;
}

/**
 * A unit cost by day with its trailing average. The average is spend over
 * the last `trailingDays` days divided by the count over the same days. A day
 * with no activity adds nothing to either sum, so it does not pull the
 * average toward zero.
 *
 * `perActivePersonDay` divides by each day's count of distinct active
 * people, so it is cost per person per active day: label it that way, never
 * "per active person".
 */
export function selectUnitCostSeries(
  data: DashboardView,
  win: WindowState,
  metric: UnitCostMetric,
  trailingDays = 7
): UnitCostSeries {
  const chat = data.chat;
  let bottom: Record<string, number> | undefined;
  let quality: { approx?: boolean; note?: string } = {};
  if (metric === 'perMessage') bottom = chat?.daily_messages;
  else if (metric === 'perActivePersonDay') bottom = dailyActivePeople(chat).map;
  else ({ map: bottom, quality } = conversationSeries(data));

  // Start the sums before the window, so its first day already has a full
  // trailing span behind it. Stop at the last day the data runs to.
  const lead = chartDays(data, { ...win, startDate: addDays(win.startDate, -(trailingDays - 1)) });
  const top = lead.map((d) => chat?.daily_cost?.[d] || 0);
  const under = lead.map((d) => bottom?.[d] || 0);
  const first = lead.findIndex((d) => d >= win.startDate);
  const from = first < 0 ? lead.length : first;
  return {
    days: lead.slice(from),
    daily: top.slice(from).map((t, i) => ratio(t, under[from + i])),
    trailing: trailingRatio(top, under, trailingDays).slice(from),
    trailingDays,
    available: chat?.daily_cost !== undefined && bottom !== undefined,
    ...quality,
  };
}

// ─── tokens, requests, time saved ────────────────────────────────────────

export interface Tokens {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  /** Share of input read from the cache, 0 to 1. Null when there is no input. */
  cacheHitRatio: number | null;
  scope: Scope;
  approx?: boolean;
  note?: string;
}

/** Token counts and the cache hit ratio. Follows the window on a snapshot with daily token counts. */
export function selectTokens(data: DashboardView, win: WindowState): Tokens {
  const chat = data.chat;
  let input: number;
  let output: number;
  let cacheRead: number;
  let cacheWrite: number;
  let scope: Scope;
  let quality: { approx?: boolean; note?: string } = {};
  if (chat?.daily_input_tokens) {
    input = sumDailyInWindow(chat.daily_input_tokens, win);
    output = sumDailyInWindow(chat.daily_output_tokens, win);
    cacheRead = sumDailyInWindow(chat.daily_cache_read_tokens, win);
    cacheWrite = sumDailyInWindow(chat.daily_cache_creation_tokens, win);
    scope = 'window';
    quality = leftOut(data, 'chat.daily_input_tokens');
  } else {
    const t = chat?.totals;
    input = t?.input_tokens || 0;
    output = t?.output_tokens || 0;
    cacheRead = t?.cache_read_tokens || 0;
    cacheWrite = t?.cache_creation_tokens || 0;
    scope = 'all-time';
  }
  return {
    input,
    output,
    cacheRead,
    cacheWrite,
    cacheHitRatio: ratio(cacheRead, input + cacheRead + cacheWrite),
    scope,
    ...quality,
  };
}

export interface Requests {
  /** Calls to a model. */
  requests: Figure;
  /** Calls that failed. */
  errors: Figure;
  /** Errors over requests, 0 to 1. Null when there are no requests. */
  errorRate: Figure<number | null>;
}

/** Model requests and failures. Follows the window on a snapshot with daily request counts. */
export function selectRequests(data: DashboardView, win: WindowState): Requests {
  const chat = data.chat;
  if (chat?.daily_requests) {
    const quality = leftOut(data, 'chat.daily_requests');
    const requests = sumDailyInWindow(chat.daily_requests, win);
    const errors = sumDailyInWindow(chat.daily_errors, win);
    return {
      requests: fig(requests, 'window', quality),
      errors: fig(errors, 'window', quality),
      errorRate: fig(ratio(errors, requests), 'window', quality),
    };
  }
  const requests = chat?.totals?.requests || 0;
  const errors = chat?.totals?.errors || 0;
  return {
    requests: fig(requests, 'all-time'),
    errors: fig(errors, 'all-time'),
    errorRate: fig(ratio(errors, requests), 'all-time'),
  };
}

const TIME_SAVED_RULE = "Each scheduled run of an agent counts as that agent's estimated minutes saved.";

/**
 * Time saved, in minutes, by the rollup's one rule: each scheduled run of an
 * agent saves that agent's estimated minutes.
 *
 * On a snapshot with the daily record this is the window's figure, counted
 * over run records. Run records start around May 2026 on an older stack, so a
 * window that reaches further back is short and says so. On a snapshot
 * without the daily record it is the lifetime total, tagged `all-time`.
 *
 * The window figure and the lifetime total are counted from two different
 * records (dated run records, and each schedule's own counter). They do not
 * add up to each other. Never show one as a part of the other.
 */
export function selectTimeSaved(data: DashboardView, win: WindowState): Figure {
  const daily = data.impact?.daily_time_saved_minutes;
  if (!daily) return selectLifetimeTimeSaved(data);
  let quality = leftOut(data, 'impact.daily_time_saved_minutes');
  let firstRecord: string | undefined;
  for (const d of Object.keys(data.scheduled_runs?.daily_runs || {}))
    if (!firstRecord || d < firstRecord) firstRecord = d;
  if (firstRecord && win.startDate < firstRecord) {
    quality = {
      approx: true,
      note: `Run records start on ${firstRecord}. Time saved before that day is not in this number.`,
    };
  }
  return fig(sumDailyInWindow(daily, win), 'window', { approx: quality.approx, note: quality.note ?? TIME_SAVED_RULE });
}

/**
 * Time saved over the life of the stacks, in minutes: each schedule's own run
 * counter times its agent's estimated minutes. Always `all-time`. Core data.
 */
export function selectLifetimeTimeSaved(data: DashboardView): Figure {
  return fig(data.impact?.total_time_saved_minutes || 0, 'all-time', { note: TIME_SAVED_RULE });
}

// ─── scheduled work ──────────────────────────────────────────────────────

export interface Scheduled {
  /**
   * Scheduled and triggered runs inside the window, counted from run records.
   * This is the one windowed count of "scheduled runs". It is not the count
   * of conversations a schedule started (`selectChat`), and not a schedule's
   * lifetime counter.
   */
  runs: Figure;
  /** Model cost of those runs. */
  cost: Figure;
  /** Runs that failed. */
  failed: Figure;
  /** Failed over runs, 0 to 1, both for the same period. Null when there are no runs. */
  failureRate: Figure<number | null>;
  /** Schedules that exist. */
  schedules: Figure;
  /** Schedules that are switched on and due to run again. */
  activeSchedules: Figure;
  /** Schedules whose last run ended in an error. */
  schedulesWithErrors: Figure;
  /** Runs a month the active schedules add up to. */
  projectedRunsPerMonth: Figure;
}

/** Scheduled runs and the state of the schedules. Core data only. */
export function selectScheduled(data: DashboardView, win: WindowState): Scheduled {
  const sr = data.scheduled_runs;
  const runs = sumDailyInWindow(sr?.daily_runs, win);
  let failed: Figure;
  let failureRate: Figure<number | null>;
  if (sr?.daily_failed_runs) {
    const quality = leftOut(data, 'scheduled_runs.daily_failed_runs');
    const n = sumDailyInWindow(sr.daily_failed_runs, win);
    failed = fig(n, 'window', quality);
    failureRate = fig(ratio(n, runs), 'window', quality);
  } else {
    // No failures by day: the lifetime count over the lifetime run count.
    // Never a lifetime count over the window's runs.
    const n = sr?.by_status?.failed || 0;
    failed = fig(n, 'all-time');
    failureRate = fig(ratio(n, sr?.totals?.count || 0), 'all-time');
  }
  return {
    runs: fig(runs, 'window'),
    cost: fig(sumDailyInWindow(sr?.daily_cost, win), 'window'),
    failed,
    failureRate,
    schedules: fig(data.schedules?.count || 0, 'now'),
    activeSchedules: fig(data.schedules?.by_derived_status?.active || 0, 'now'),
    schedulesWithErrors: fig(data.schedules?.schedules_with_errors || 0, 'now'),
    projectedRunsPerMonth: fig(data.schedules?.projected_runs_per_month_total || 0, 'now'),
  };
}

// ─── credits ─────────────────────────────────────────────────────────────

export interface Credits {
  /** Credits used inside the window. */
  window: Figure;
  /** The same in USD, at the credit price. */
  windowUsd: Figure;
  /** Credits used in the current New Zealand billing month. */
  month: Figure;
  /** The billing month `month` covers, `YYYY-MM`. */
  monthLabel: string;
  /** The month's allocation. Null when none is set. */
  allocation: Figure | null;
  /**
   * One stack: the top-up balance. Below zero it is owed only when
   * `collectible` is true. Null on a summed view.
   */
  balance: Figure | null;
  /** A summed view: credit that clients on credits paid for and did not use yet. Null on one stack. */
  prepaid: Figure | null;
  /** A summed view: credits that clients on credits owe. Null on one stack. */
  owed: Figure | null;
  /**
   * Credits used this month past the allocation by stacks Arcanum does not
   * invoice for credits. Never owed. On one stack: null when the stack is one
   * Arcanum invoices, because its use past the allocation is in `balance`.
   */
  notBilledOver: Figure | null;
  /** One stack: Arcanum can invoice it for credits. Null on a summed view. */
  collectible: boolean | null;
  /** One stack: why its use past the allocation is not billed. Empty when it is collectible. */
  notBilledReason: string;
  /** A summed view: how many of its metered stacks Arcanum can invoice. Null on one stack. */
  collectibleStacks: number | null;
  /** How many stacks in the selection meter credits. */
  stacksMetered: number;
  /** Stacks left out of `month` because their last update is from an older billing month. */
  stacksBehind: string[];
  /** The month metering started, `YYYY-MM`. One stack only. */
  meteringSince?: string;
  /** Lifetime credits by what used them: chat, agent, scheduled, app, ... */
  bySource: Record<string, number>;
  /** USD per credit. */
  creditUsd: number;
}

const DEFAULT_CREDIT_USD = 0.3;

/**
 * Credits for the selection, or null when no stack in it meters credits.
 *
 * The snapshot keeps credits by day for the last four billing months and by
 * month for all time. A window that reaches further back adds the whole
 * months before the daily record; a month the window only partly covers
 * cannot be split, and the figure then says so.
 *
 * `contract` is the stack's contract when one stack is selected. Without it
 * the contract is read from the snapshot alone and the stack counts as active.
 */
export function selectCredits(data: DashboardView, win: WindowState, contract?: StackContract): Credits | null {
  let daily: Record<string, number> | undefined;
  let months: Record<string, number> = {};
  let creditUsd = DEFAULT_CREDIT_USD;
  const billingMonth = nzBillingMonth();
  let base: Omit<Credits, 'window' | 'windowUsd'>;

  if (isAggregate(data)) {
    const cr = data.credits_rollup;
    if (!cr || cr.clients_with_ledger === 0) return null;
    daily = cr.daily_credits;
    months = cr.months || {};
    const behind = cr.stacks_behind || [];
    base = {
      month: fig(
        cr.current_month_used,
        'month',
        behind.length
          ? {
              note: `${behind.length === 1 ? '1 stack is' : `${behind.length} stacks are`} left out, because the last update is from an older month: ${nameList(behind)}.`,
              approx: true,
            }
          : undefined
      ),
      monthLabel: cr.current_month || billingMonth,
      allocation: cr.current_month_allocation ? fig(cr.current_month_allocation, 'month') : null,
      balance: null,
      prepaid: fig(cr.prepaid_total, 'now'),
      owed: fig(cr.owed_total, 'now'),
      notBilledOver: cr.not_billed_over === undefined ? null : fig(cr.not_billed_over, 'month'),
      collectible: null,
      notBilledReason: '',
      collectibleStacks: cr.collectible_stacks ?? null,
      stacksMetered: cr.clients_with_ledger,
      stacksBehind: behind,
      bySource: cr.by_source || {},
      creditUsd,
    };
  } else {
    const cr = data.credits;
    if (!cr?.available) return null;
    daily = cr.daily_credits;
    for (const [m, v] of Object.entries(cr.months || {})) months[m] = v?.credits_charged || 0;
    creditUsd = cr.credit_usd ?? DEFAULT_CREDIT_USD;
    // The snapshot's own month. When the update is behind, that is an older
    // month: the tile shows which, so last month is never read as this one.
    const isCurrent = !cr.current_month || cr.current_month === billingMonth;
    const behind = isCurrent
      ? undefined
      : {
          note: `This is ${cr.current_month}, the month of the last update. No update has arrived for ${billingMonth}.`,
          approx: true,
        };
    const terms = contract ?? snapshotContract(data);
    base = {
      month: fig(cr.current_month_used || 0, 'month', behind),
      monthLabel: cr.current_month || billingMonth,
      allocation: cr.current_month_allocation ? fig(cr.current_month_allocation, 'month') : null,
      balance: cr.standing ? fig(cr.standing.available_balance, 'now') : null,
      prepaid: null,
      owed: null,
      notBilledOver: terms.collectible ? null : fig(overAllocation(cr), 'month', behind),
      collectible: terms.collectible,
      notBilledReason: notBilledReason(terms),
      collectibleStacks: null,
      stacksMetered: 1,
      stacksBehind: isCurrent ? [] : [data.client],
      meteringSince: cr.first_month || undefined,
      bySource: cr.by_source || {},
      creditUsd,
    };
  }

  let used = sumDailyInWindow(daily, win);
  let quality: { approx?: boolean; note?: string } = {};
  let firstDaily: string | undefined;
  for (const d of Object.keys(daily || {})) if (!firstDaily || d < firstDaily) firstDaily = d;
  if (firstDaily && win.startDate < firstDaily) {
    const firstDailyMonth = firstDaily.slice(0, 7);
    let partial = false;
    for (const [month, credits] of Object.entries(months)) {
      if (month >= firstDailyMonth || !credits) continue;
      const monthStart = `${month}-01`;
      const monthEnd = addDays(`${nextMonth(month)}-01`, -1);
      if (monthStart >= win.startDate && monthEnd <= win.endDate) used += credits;
      else if (monthEnd >= win.startDate && monthStart <= win.endDate) partial = true;
    }
    if (partial) {
      quality = {
        approx: true,
        note: `Credits are kept by day from ${firstDaily}. The window starts part-way through an earlier month, and that month is left out.`,
      };
    }
  }

  return { ...base, window: fig(used, 'window', quality), windowUsd: fig(used * creditUsd, 'window', quality) };
}

function nextMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return m === 12 ? `${y + 1}-01` : `${y}-${String(m + 1).padStart(2, '0')}`;
}

// ─── tools and models ────────────────────────────────────────────────────

/** A tool name as a person reads it: `mcp__numa__numa_tool` reads `numa: numa_tool`. */
export function toolLabel(tool: string): string {
  const m = /^mcp__([^_]+(?:_[^_]+)*?)__(.+)$/.exec(tool);
  return m ? `${m[1]}: ${m[2]}` : tool;
}

export interface TopTools {
  /** Most called first. */
  rows: Array<{ tool: string; label: string; calls: number; share: number }>;
  /** Calls to every tool, listed or not. */
  total: number;
  /** How many tools were called in all. */
  distinct: number;
  scope: Scope;
  approx?: boolean;
  note?: string;
}

/**
 * The tools called most. Follows the window on a snapshot that records tool
 * calls by tool and day; otherwise it is the lifetime count, tagged so.
 */
export function selectTopTools(data: DashboardView, win: WindowState, limit = 20): TopTools {
  const byDay = data.chat?.daily_tool_calls_by_tool;
  const totals = byDay ? sumByKeyInWindow(byDay, win) : data.chat?.tool_totals || {};
  const entries = Object.entries(totals).filter(([, n]) => (n || 0) > 0);
  const total = entries.reduce((a, [, n]) => a + n, 0);
  const rows = entries
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([tool, calls]) => ({ tool, label: toolLabel(tool), calls, share: total > 0 ? calls / total : 0 }));
  return {
    rows,
    total,
    distinct: entries.length,
    scope: byDay ? 'window' : 'all-time',
    ...(byDay ? leftOut(data, 'chat.daily_tool_calls_by_tool') : {}),
  };
}

export interface ModelRow {
  /** The model's name with no region or maker in front: `claude-sonnet-4-6`. */
  model: string;
  /** The raw ids merged into the row, one per region the model ran through. */
  ids: string[];
  cost: number;
  conversations: number;
  /** Share of the listed models' cost, 0 to 1. */
  shareOfCost: number;
  /** Share of the listed models' conversations, 0 to 1. */
  shareOfConversations: number;
}

export interface Models {
  /** Most costly first. One row per model, whatever region it ran through. */
  rows: ModelRow[];
  totalCost: number;
  totalConversations: number;
  scope: Scope;
  /** True when conversations are counted once per active day. */
  conversationsApprox: boolean;
  note?: string;
}

/**
 * Models in use, with both shares. A conversation counts for one model: the
 * one it ended on. Cost of sub-agents and side calls is on that model too.
 */
export function selectModels(data: DashboardView, win: WindowState): Models {
  const { totals, windowed, convsExact } = modelsInWindow(data.chat, win);
  const behind = data.portal?.missing?.['chat.daily_convs_started'] ?? [];
  const exact = convsExact && behind.length === 0;
  let totalCost = 0;
  let totalConversations = 0;
  for (const v of Object.values(totals)) {
    totalCost += v.cost;
    totalConversations += v.convs;
  }
  const rows = Object.entries(totals)
    .map(([model, v]) => ({
      model,
      ids: v.ids,
      cost: v.cost,
      conversations: v.convs,
      shareOfCost: totalCost > 0 ? v.cost / totalCost : 0,
      shareOfConversations: totalConversations > 0 ? v.convs / totalConversations : 0,
    }))
    .sort((a, b) => b.cost - a.cost || b.conversations - a.conversations);
  return {
    rows,
    totalCost,
    totalConversations,
    scope: windowed ? 'window' : 'all-time',
    conversationsApprox: windowed && !exact,
    note: windowed && !exact ? COUNTED_PER_DAY : undefined,
  };
}

// ─── counts that do not follow the window ────────────────────────────────

export interface Counts {
  /** Every agent: the ones shared with a workspace and the ones a person built for themselves. */
  agents: Figure;
  /** Agents shared with a workspace. */
  workspaceAgents: Figure;
  /** Agents a person built for themselves. */
  personalAgents: Figure;
  knowledgeBases: Figure;
  dataConnectors: Figure;
  /** Connections people made to outside tools (Gmail, Slack, ...). */
  integrationConnections: Figure;
  /** People with one connected tool or more. The value is null on a snapshot that does not record it. */
  peopleWithIntegrations: Figure<number | null>;
  /** Outside tools in use. On a summed view this needs the `inventory` tier to count each tool once. */
  distinctIntegrations: Figure;
}

/** How many of each thing exists, as of the last nightly update. Core data only. */
export function selectCounts(data: DashboardView): Counts {
  const pd = data.pipedream;
  const exactApps = !isAggregate(data) || (pd?.by_app_active?.length || 0) > 0 || !pd?.distinct_active_apps;
  return {
    agents: fig((data.agents?.agents?.count || 0) + (data.agents?.user_agents?.count || 0), 'now'),
    workspaceAgents: fig(data.agents?.agents?.count || 0, 'now'),
    personalAgents: fig(data.agents?.user_agents?.count || 0, 'now'),
    knowledgeBases: fig(data.integrations?.knowledge_bases?.count || 0, 'now'),
    dataConnectors: fig(data.integrations?.data_connectors?.count || 0, 'now'),
    integrationConnections: fig(pd?.active_connections || 0, 'now'),
    // `users_with_data` is "the lookup worked for this person", not "has a
    // connection", so it is never shown in this figure's place.
    peopleWithIntegrations:
      pd?.users_with_apps === undefined
        ? fig<number | null>(null, 'now', { note: 'Not recorded on this data yet.' })
        : fig<number | null>(pd.users_with_apps, 'now', leftOut(data, 'pipedream.users_with_apps')),
    distinctIntegrations: fig(
      pd?.distinct_active_apps || 0,
      'now',
      exactApps
        ? undefined
        : {
            approx: true,
            note: 'A tool used in several stacks is counted once for each stack until the full lists load.',
          }
    ),
  };
}

// ─── what is switched on ─────────────────────────────────────────────────

/** The switches a stack's config carries. Each value is one setting of one stack, as of the last nightly update. */
export interface Switches {
  /** People can build apps with the App Builder. Null when the nightly record does not say. */
  appBuilder: boolean | null;
  /** The stack has the integrations service, so people can connect outside tools. */
  integrations: boolean;
  /** People see the credits page and the credit count in chat. Use is metered either way. */
  creditsShown: boolean;
  /** What search in Numa Files runs on. Null when the config does not say. */
  filesSearch: 'bedrock' | 'q' | 'off' | null;
  /** Other stacks may run their model use on this account's quota. */
  lendsQuota: boolean;
  /** The AWS account whose model quota this stack uses in place of its own. */
  usesQuotaOf: string | null;
}

/** One stack's switches. */
export function selectSwitches(stack: ClientSnapshot): Switches {
  const cfg = stack.client_config || {};
  const kb = cfg.preferred_kb;
  return {
    appBuilder: cfg.app_builder ?? null,
    integrations: stack.pipedream?.skipped !== 'no_relay_function',
    // A config with no setting takes the default, which is to show credits.
    creditsShown: cfg.show_credits !== false,
    filesSearch: kb === 'bedrock' || kb === 'q' ? kb : kb === 'none' ? 'off' : null,
    lendsQuota: Boolean(cfg.allow_bedrock_quota_sharing),
    usesQuotaOf: cfg.bedrock_account || null,
  };
}

export interface SetupRow {
  client: string;
  label: string;
  provisioned: Figure;
  counts: Counts;
  switches: Switches;
}

export interface Setup {
  /** One row per stack, in the order given. Infrastructure accounts have no setup and are left out. */
  rows: SetupRow[];
  /** How many of the stacks have each switch on. `appBuilderUnknown` is the stacks whose record does not say. */
  on: {
    appBuilder: number;
    appBuilderUnknown: number;
    integrations: number;
    creditsShown: number;
    lendsQuota: number;
    usesAnothersQuota: number;
    filesSearch: Record<'bedrock' | 'q' | 'off' | 'unknown', number>;
  };
}

/**
 * What each stack has switched on and connected, and how many stacks have
 * each switch on. Everything here is as of the last nightly update. Core
 * data only.
 */
export function selectSetup(stacks: ClientSnapshot[]): Setup {
  const rows: SetupRow[] = stacks
    .filter((s) => !isInfraAccount(s))
    .map((s) => ({
      client: s.client,
      label: stackLabel(s),
      provisioned: selectProvisionedUsers(s),
      counts: selectCounts(s),
      switches: selectSwitches(s),
    }));
  const on: Setup['on'] = {
    appBuilder: 0,
    appBuilderUnknown: 0,
    integrations: 0,
    creditsShown: 0,
    lendsQuota: 0,
    usesAnothersQuota: 0,
    filesSearch: { bedrock: 0, q: 0, off: 0, unknown: 0 },
  };
  for (const { switches } of rows) {
    if (switches.appBuilder) on.appBuilder++;
    if (switches.appBuilder === null) on.appBuilderUnknown++;
    if (switches.integrations) on.integrations++;
    if (switches.creditsShown) on.creditsShown++;
    if (switches.lendsQuota) on.lendsQuota++;
    if (switches.usesQuotaOf) on.usesAnothersQuota++;
    on.filesSearch[switches.filesSearch ?? 'unknown']++;
  }
  return { rows, on };
}

// ─── per-person rows ─────────────────────────────────────────────────────

export interface UserRow {
  client: string;
  userId: string;
  email?: string;
  /** The email, or a short form of the id when the person is no longer in the directory. */
  label: string;
  cost: number;
  messages: number;
  conversations: number;
  toolCalls: number;
  turns: number;
  avgSpanSeconds: number | null;
  /** Null when there are no messages. */
  costPerMessage: number | null;
  /** Null when there are no conversations, or when cost and conversations cover different periods. */
  costPerConversation: number | null;
  /** Share of the listed people's cost, 0 to 1. */
  shareOfCost: number;
  /** Days inside the window on which the person used Numa themselves. */
  activeDays: number;
  /** True when the person used Numa themselves inside the window. The same test as `selectActiveUsers`. */
  activeInWindow: boolean;
  /** First activity on record, and the last time the person used Numa themselves. Lifetime. */
  firstAt: string | null;
  lastAt: string | null;
  /** True for a person with a login who has never sent a message. Every number on the row is 0. */
  neverActive: boolean;
}

export interface UserRows {
  /**
   * One row per person, most costly first: everyone with a login, and anyone
   * with a message on record. The same people as the engagement map. A person
   * with nothing in the window is listed with zeros: filter on `messages > 0`
   * for "active in the window", and on `!neverActive` for "has used Numa".
   */
  rows: UserRow[];
  /** How many of the rows are people with a login who never sent a message. */
  neverActive: number;
  totalCost: number;
  /** The period each column covers. Tag any column that is not `window`. */
  scopes: Record<'cost' | 'messages' | 'conversations' | 'toolCalls' | 'turns' | 'avgSpan', Scope>;
  approx?: boolean;
  note?: string;
}

/**
 * Per-person rows for the stacks given. Needs the `users` tier. A column
 * follows the window when every stack carries its daily record; otherwise
 * the whole column is the lifetime figure and `scopes` says so.
 */
export function selectUserRows(stacks: ClientSnapshot[], win: WindowState): Tiered<UserRows> {
  const need = tiersNeededAcross(
    stacks,
    'chat.by_user',
    'chat.daily_cost_by_user',
    'chat.daily_messages_by_user',
    'chat.daily_convs_started_by_user',
    'chat.daily_tool_calls_by_user',
    'chat.daily_turns_by_user',
    'chat.daily_span_seconds_sum_by_user',
    'chat.daily_span_count_by_user',
    'chat.daily_adhoc_messages_by_user',
    'users.sub_to_email'
  );
  if (need.length) return { needs: need };
  const withUsers = stacks.filter((s) => Object.keys(s.chat?.by_user || {}).length > 0);
  type Field =
    | 'daily_cost_by_user'
    | 'daily_messages_by_user'
    | 'daily_convs_started_by_user'
    | 'daily_tool_calls_by_user'
    | 'daily_turns_by_user'
    | 'daily_span_seconds_sum_by_user'
    | 'daily_span_count_by_user';
  const everyStackHas = (f: Field) => withUsers.length > 0 && withUsers.every((s) => s.chat?.[f] !== undefined);
  const scopeOf = (f: Field): Scope => (everyStackHas(f) ? 'window' : 'all-time');
  const scopes: UserRows['scopes'] = {
    cost: scopeOf('daily_cost_by_user'),
    messages: scopeOf('daily_messages_by_user'),
    conversations: scopeOf('daily_convs_started_by_user'),
    toolCalls: scopeOf('daily_tool_calls_by_user'),
    turns: scopeOf('daily_turns_by_user'),
    avgSpan:
      everyStackHas('daily_span_seconds_sum_by_user') && everyStackHas('daily_span_count_by_user')
        ? 'window'
        : 'all-time',
  };

  const rows: UserRow[] = [];
  let quality: { approx?: boolean; note?: string } = {};
  let countsSchedules = false;
  for (const s of stacks) {
    const chat = s.chat;
    const q = beforeDetail(s, win, 'The per-person record');
    if (q.approx) quality = q;
    const cost = sumByKeyInWindow(chat?.daily_cost_by_user, win);
    const messages = sumByKeyInWindow(chat?.daily_messages_by_user, win);
    const convs = sumByKeyInWindow(chat?.daily_convs_started_by_user, win);
    const tools = sumByKeyInWindow(chat?.daily_tool_calls_by_user, win);
    const turns = sumByKeyInWindow(chat?.daily_turns_by_user, win);
    const span = sumByKeyInWindow(chat?.daily_span_seconds_sum_by_user, win);
    const spanCount = sumByKeyInWindow(chat?.daily_span_count_by_user, win);
    const typed = peopleActivityMap(chat);
    if (!typed.exact && typed.map) countsSchedules = true;
    // Everyone on record, as the engagement map counts them: a login with no
    // message is a person too, and a table titled "everyone" must list them.
    const ids = new Set<string>([
      ...Object.keys(chat?.by_user || {}),
      ...Object.keys(cost),
      ...Object.keys(messages),
      ...Object.keys(s.users?.sub_to_email || {}),
    ]);
    for (const userId of ids) {
      const all = chat?.by_user?.[userId];
      const email = s.users?.sub_to_email?.[userId];
      const rowCost = scopes.cost === 'window' ? cost[userId] || 0 : all?.cost || 0;
      const rowMessages = scopes.messages === 'window' ? messages[userId] || 0 : all?.user_messages || 0;
      const rowConvs = scopes.conversations === 'window' ? convs[userId] || 0 : all?.convs || 0;
      let activeDays = 0;
      for (const [d, v] of Object.entries(typed.map?.[userId] || {})) {
        if (d >= win.startDate && d <= win.endDate && (v || 0) > 0) activeDays++;
      }
      rows.push({
        client: s.client,
        userId,
        email,
        label: email || `${userId.slice(0, 8)}…`,
        cost: rowCost,
        messages: rowMessages,
        conversations: rowConvs,
        toolCalls: scopes.toolCalls === 'window' ? tools[userId] || 0 : all?.tool_calls || 0,
        turns: scopes.turns === 'window' ? turns[userId] || 0 : all?.turns || 0,
        avgSpanSeconds:
          scopes.avgSpan === 'window'
            ? ratio(span[userId] || 0, spanCount[userId] || 0)
            : (all?.avg_span_seconds ?? null),
        costPerMessage: scopes.cost === scopes.messages ? ratio(rowCost, rowMessages) : null,
        costPerConversation: scopes.cost === scopes.conversations ? ratio(rowCost, rowConvs) : null,
        shareOfCost: 0,
        activeDays,
        activeInWindow: activeDays > 0,
        firstAt: all?.first_at ?? null,
        // The person's own last use when the snapshot can tell, never a schedule's.
        lastAt: (typed.exact ? all?.adhoc_last_at : all?.last_at) ?? null,
        neverActive: !all && !(userId in cost) && !(userId in messages),
      });
    }
  }
  const neverActive = rows.filter((r) => r.neverActive).length;
  const totalCost = rows.reduce((a, r) => a + r.cost, 0);
  for (const r of rows) r.shareOfCost = totalCost > 0 ? r.cost / totalCost : 0;
  rows.sort((a, b) => b.cost - a.cost || b.messages - a.messages);
  if (countsSchedules) {
    quality = { approx: true, note: [SCHEDULE_COUNTS_AS_ACTIVE, quality.note].filter(Boolean).join(' ') };
  }
  return { rows, neverActive, totalCost, scopes, ...quality };
}

// ─── per-agent rows ──────────────────────────────────────────────────────

export interface AgentUsageRow {
  client: string;
  agentId: string;
  /** `<client>::<agentId>`. Unique across stacks, where the id alone is not. Use it as the React key. */
  key: string;
  title: string;
  cost: number;
  scheduledCost: number;
  adhocCost: number;
  /**
   * Conversations a schedule or a trigger started with the agent. Counted
   * from chat traces, as `scheduledCost` is, so the two divide cleanly. This
   * is not the count of run records: label it "scheduled conversations",
   * never "scheduled runs".
   */
  scheduledConversations: number;
  /** Conversations a person started with the agent. */
  adhocConversations: number;
  /** Null when there are none, or when cost and count cover different periods. */
  costPerScheduledConversation: number | null;
  costPerAdhocConversation: number | null;
  /** Null until the data it is worked out from is loaded (see `pending`). */
  timeSavedMinutes: number | null;
  /** Lifetime. */
  turns: number;
  toolCalls: number;
  messages: number;
  firstAt: string | null;
  lastAt: string | null;
  optimiseSessions: number;
  lastOptimisedAt: string | null;
}

export interface AgentRows {
  /** One row per agent per stack, most costly first. An agent with nothing in the window is listed with zeros. */
  rows: AgentUsageRow[];
  /** The period each group of columns covers. */
  scopes: Record<'cost' | 'conversations' | 'timeSaved', Scope>;
  /** Tiers that would fill the cells still null. */
  pending: FleetTier[];
  approx?: boolean;
  note?: string;
}

/**
 * Per-agent rows for the stacks given. Needs the `agents` tier. Rows are
 * built stack by stack, so a library agent installed in 14 stacks is 14 rows
 * and no stack's numbers overwrite another's.
 */
export function selectAgentRows(stacks: ClientSnapshot[], win: WindowState): Tiered<AgentRows> {
  const need = tiersNeededAcross(
    stacks,
    'chat.by_agent',
    'chat.daily_cost_by_agent',
    'chat.daily_scheduled_cost_by_agent',
    'chat.daily_adhoc_cost_by_agent',
    'chat.daily_scheduled_count_by_agent',
    'chat.daily_adhoc_count_by_agent',
    'impact.per_agent_runs',
    'impact.daily_time_saved_minutes_by_agent'
  );
  if (need.length) return { needs: need };
  const withAgents = stacks.filter((s) => (s.chat?.by_agent || []).length > 0);
  const every = (test: (s: ClientSnapshot) => boolean) => withAgents.length > 0 && withAgents.every(test);
  const costWindowed = every((s) => s.chat?.daily_cost_by_agent !== undefined);
  const runsWindowed = every(
    (s) => s.chat?.daily_scheduled_count_by_agent !== undefined && s.chat?.daily_adhoc_count_by_agent !== undefined
  );
  const withSaved = stacks.filter((s) => (s.impact?.total_time_saved_minutes || 0) > 0);
  const savedWindowed =
    withSaved.length > 0 && withSaved.every((s) => s.impact?.daily_time_saved_minutes_by_agent !== undefined);
  // The lifetime fallback multiplies an agent's runs by its estimated minutes, which sit in the inventory lists.
  const pending = savedWindowed ? [] : tiersNeededAcross(stacks, 'agents.agents.rows', 'agents.user_agents.rows');

  const rows: AgentUsageRow[] = [];
  let quality: { approx?: boolean; note?: string } = {};
  for (const s of stacks) {
    const chat = s.chat;
    const q = beforeDetail(s, win, 'The per-agent record');
    if (q.approx) quality = q;
    const cost = sumByKeyInWindow(chat?.daily_cost_by_agent, win);
    const schedCost = sumByKeyInWindow(chat?.daily_scheduled_cost_by_agent, win);
    const adhocCost = sumByKeyInWindow(chat?.daily_adhoc_cost_by_agent, win);
    const schedRuns = sumByKeyInWindow(chat?.daily_scheduled_count_by_agent, win);
    const adhocConvs = sumByKeyInWindow(chat?.daily_adhoc_count_by_agent, win);
    const saved = sumByKeyInWindow(s.impact?.daily_time_saved_minutes_by_agent, win);
    const minutes: Record<string, number> = {};
    const titles: Record<string, string> = {};
    for (const a of [...(s.agents?.agents?.rows || []), ...(s.agents?.user_agents?.rows || [])]) {
      if (!a.agent_id) continue;
      minutes[a.agent_id] = a.estimated_time_saved_minutes || 0;
      if (a.title) titles[a.agent_id] = a.title;
    }
    const inventoryLoaded = tiersNeededFor(s, 'agents.agents.rows', 'agents.user_agents.rows').length === 0;

    for (const a of chat?.by_agent || []) {
      const id = a.agent_id;
      const rowSched = costWindowed ? schedCost[id] || 0 : a.scheduled_cost || 0;
      const rowAdhoc = costWindowed ? adhocCost[id] || 0 : a.adhoc_cost || 0;
      const rowSchedRuns = runsWindowed ? schedRuns[id] || 0 : a.scheduled_count || 0;
      const rowAdhocConvs = runsWindowed ? adhocConvs[id] || 0 : a.adhoc_count || 0;
      let timeSavedMinutes: number | null;
      if (savedWindowed) timeSavedMinutes = saved[id] || 0;
      else if (inventoryLoaded) timeSavedMinutes = (s.impact?.per_agent_runs?.[id] || 0) * (minutes[id] || 0);
      else timeSavedMinutes = null;
      rows.push({
        client: s.client,
        agentId: id,
        key: agentKey(s.client, id),
        title: a.agent_title || titles[id] || id,
        cost: costWindowed ? cost[id] || 0 : a.cost || 0,
        scheduledCost: rowSched,
        adhocCost: rowAdhoc,
        scheduledConversations: rowSchedRuns,
        adhocConversations: rowAdhocConvs,
        costPerScheduledConversation: costWindowed === runsWindowed ? ratio(rowSched, rowSchedRuns) : null,
        costPerAdhocConversation: costWindowed === runsWindowed ? ratio(rowAdhoc, rowAdhocConvs) : null,
        timeSavedMinutes,
        turns: a.turns || 0,
        toolCalls: a.tool_calls || 0,
        messages: a.user_messages || 0,
        firstAt: a.first_at ?? null,
        lastAt: a.last_at ?? null,
        optimiseSessions: a.optimise_sessions || 0,
        lastOptimisedAt: a.last_optimised_at ?? null,
      });
    }
  }
  rows.sort(
    (a, b) =>
      b.cost - a.cost ||
      b.scheduledConversations + b.adhocConversations - (a.scheduledConversations + a.adhocConversations)
  );
  return {
    rows,
    scopes: {
      cost: costWindowed ? 'window' : 'all-time',
      conversations: runsWindowed ? 'window' : 'all-time',
      timeSaved: savedWindowed ? 'window' : 'all-time',
    },
    pending,
    ...quality,
  };
}

// ─── per-stack rows ──────────────────────────────────────────────────────

export interface StackRow {
  /** The selection id: the client name, or `_infra:<alias>`. Pass it to `onSelectClient`. */
  client: string;
  /** What a person reads. */
  label: string;
  kind: StackKind;
  org: 'nextgen' | 'arcanum' | 'standalone' | null;
  region?: string;
  accountId?: string;
  /**
   * Other stacks in the list on the same AWS account. AWS bills by account,
   * so `spend` and `awsBill` repeat the account's bill on each of them: the
   * rows of a shared account do not add up to the account. Ask for one row
   * per account (`onePerAccount`) to have them folded, or use `selectSpend`
   * on the view for a total. Empty on a folded row.
   */
  sharesAccountWith: string[];
  /**
   * The stacks this row stands for, as selection ids. One entry, the row's
   * own `client`, unless the row is a shared AWS account folded into one
   * (`onePerAccount`): then every stack on the account, largest first.
   */
  members: string[];
  /** What the stack cost to run in the window. Null while the data it needs loads (see `pending`). */
  spend: number | null;
  /** The AWS bill in the window. For a standalone customer: the Numa part of it. */
  awsBill: number;
  /** Chat and agent model use, priced from tokens. */
  chatCost: number;
  conversations: number;
  /** True when this stack counts a conversation once per active day. */
  conversationsApprox: boolean;
  messages: number;
  /** People who used Numa themselves in the window. Null until the `users` tier is loaded. */
  activeUsers: number | null;
  provisionedUsers: number;
  /**
   * Unit costs for the window, by the definitions every tab uses. Each is
   * null when there is nothing to divide by, or while a part is loading.
   * `costPerActivePerson` is MODEL usage (chat and agents) over active
   * people: the figure `selectPerActivePerson` gives. Total spend over active
   * people is a different number with its own name, `spendPerActivePerson`.
   */
  costPerConversation: number | null;
  costPerMessage: number | null;
  costPerActivePerson: number | null;
  messagesPerActivePerson: number | null;
  spendPerActivePerson: number | null;
  /** Credits in the window, in the current billing month, and the balance. Null when the stack does not meter credits. */
  creditsWindow: number | null;
  /** Null as well when the stack's last update is from an older month. */
  creditsMonth: number | null;
  creditsBalance: number | null;
  schedules: number;
  activeSchedules: number;
  /** Scheduled and triggered runs in the window, from run records. */
  scheduledRuns: number;
  agents: number;
  knowledgeBases: number;
  /** Outside tools in use (Gmail, Slack, ...). */
  integrations: number;
  /** Connections people made to those tools. */
  integrationConnections: number;
  /** People with one connected tool or more. Null on a snapshot that does not record it. */
  peopleWithIntegrations: number | null;
  dataConnectors: number;
  /** Is the credits view shown to the client? Null when the config does not say (it then defaults to shown). */
  showsCredits: boolean | null;
  /** The stack lets other stacks use its model quota. */
  lendsModelQuota: boolean;
  /** The account this stack's Claude use is billed to, when it borrows quota. Null when it uses its own. */
  borrowsModelQuotaFrom: string | null;
  timeSavedMinutes: number;
  /** Published apps and dashboards. Null when the stack's snapshot does not say. */
  apps: number | null;
  /** Is the App Builder switched on? Null when the snapshot does not say. */
  appBuilder: boolean | null;
  /** App runs in the window. Null when the snapshot has no run history. */
  appRuns: number | null;
  generatedAt?: string;
  ageHours: number | null;
  /** The data is older than the nightly update allows for. */
  stale: boolean;
  /** The last nightly update failed. The row shows the last good one. */
  failed: boolean;
  error?: string;
  /**
   * The contract as its badge reads: "Credits", "Partner: Ohnyx". Null when
   * none is set. A folded account: each contract on it, joined.
   */
  contract: string | null;
  /** The badge colour of `contract`. Null on a folded account whose stacks differ. */
  contractVariant: string | null;
  /** What the contract means, for a hover. */
  contractHint?: string;
  /** Arcanum can invoice the stack for credits. `some`: a folded account where only some of its stacks can be. */
  collectible: 'yes' | 'no' | 'some';
  /** `nolia` for a Nolia stack. */
  product: 'nolia' | 'numa';
  /** The client carries the Inactive tag. A folded account: every stack on it does. */
  inactive: boolean;
}

export interface StackRows {
  /** In the order `sortedBy` names. */
  rows: StackRow[];
  /**
   * `spend`: highest spend first. `chatCost`: highest model usage first,
   * because some row's spend is still loading (see `pending`). The order by
   * model usage never changes while a tier loads; the order by spend replaces
   * it once, when every row has its spend. A list that ranks by spend should
   * wait for `spend` and not draw a ranking it will have to reorder.
   */
  sortedBy: 'spend' | 'chatCost';
  /** The period the column that can fall back covers. Every other column is as documented on `StackRow`. */
  scopes: Record<'timeSaved', Scope>;
  /** Tiers that would fill the cells still null. */
  pending: FleetTier[];
}

export interface StackRowsOptions {
  /**
   * One row per AWS account. Stacks that share an account are folded into
   * one row: the account's bill counted once, each stack's own figures
   * (model usage, conversations, people, credits, apps) added up, and the
   * stacks named in `members`. Without it a shared account's bill repeats on
   * every stack's row and a list of "largest stacks" fills with one bill
   * several times.
   */
  onePerAccount?: boolean;
  /** Each stack's contract. Without it the contract is read from the snapshot alone. */
  contractOf?: ContractOf;
  /** Clients tagged Inactive. A shared account's row is led by an active stack when it has one. */
  inactive?: ReadonlySet<string>;
}

const sumOrNull = (values: Array<number | null>): number | null => {
  const known = values.filter((v): v is number => v !== null);
  return known.length ? known.reduce((a, b) => a + b, 0) : null;
};
/** Null when any part is still loading: a sum of the parts that happen to be in would be a wrong number. */
const sumWhenAllKnown = (values: Array<number | null>): number | null =>
  values.some((v) => v === null) ? null : (values as number[]).reduce((a, b) => a + b, 0);

/**
 * One row per stack or account, for every per-client table on the dashboard.
 * Core data fills most of it at once. `spend` waits for the `services` tier
 * on a snapshot without ready-made model lines, and `activeUsers` waits for
 * the `users` tier: those cells are null until then and `pending` names the
 * tiers. Pass `fleet.freshness` to fill `failed` and `error`.
 */
export function selectStackRows(
  stacks: ClientSnapshot[],
  win: WindowState,
  freshness?: (clientName: string) => Freshness,
  options: StackRowsOptions = {}
): StackRows {
  const pending = new Set<FleetTier>();
  const billingMonth = nzBillingMonth();
  const contractOf = options.contractOf ?? snapshotContract;
  const inactive = options.inactive;
  const byAccount = new Map<string, ClientSnapshot[]>();
  for (const s of stacks) {
    const acct = s.client_config?.client_account_id;
    if (acct && !isInfraAccount(s)) byAccount.set(acct, [...(byAccount.get(acct) || []), s]);
  }
  const withSaved = stacks.filter((s) => (s.impact?.total_time_saved_minutes || 0) > 0);
  const savedWindowed =
    withSaved.length > 0 && withSaved.every((s) => s.impact?.daily_time_saved_minutes !== undefined);

  /** Spend for a view, or null with the tiers noted when its model lines are not in yet. */
  const spendOf = (view: DashboardView) => {
    const inferred = computeInferredCosts(view, win);
    let spend: number | null = inferred.totals.inferred;
    if (!inferred.modelLinesKnown) {
      const need = tiersNeededFor(view, 'cost_explorer.by_service_daily');
      if (need.length) {
        spend = null;
        need.forEach((t) => pending.add(t));
      }
    }
    return { spend, awsBill: inferred.totals.ceTotal, chatCost: inferred.totals.chat };
  };

  const rowFor = (raw: ClientSnapshot): StackRow => {
    const s = snapshotForDisplay(raw);
    const cfg = s.client_config || {};
    let activeUsers: number | null = null;
    const needPeople = tiersNeededFor(s, 'chat.daily_adhoc_messages_by_user', 'chat.daily_messages_by_user');
    const people = peopleActivityMap(s.chat);
    if (isInfraAccount(s)) {
      activeUsers = 0;
    } else if (!needPeople.length) {
      activeUsers = people.map ? activePeopleInWindow(people.map, win) : s.chat?.totals?.users || 0;
    } else {
      needPeople.forEach((t) => pending.add(t));
    }
    const cr = s.credits?.available ? s.credits : undefined;
    const fresh = freshness?.(s.client);
    const ua = s.user_apps?.available ? s.user_apps : undefined;
    const acct = cfg.client_account_id;
    const shared = acct && !isInfraAccount(s) ? (byAccount.get(acct) || []).filter((x) => x.client !== s.client) : [];
    const terms = isInfraAccount(s) ? null : contractOf(raw);
    return {
      client: s.client,
      label: stackLabel(s),
      kind: stackKind(s),
      org: cfg.account_org ?? null,
      region: cfg.region,
      accountId: acct,
      sharesAccountWith: shared.map((x) => x.client),
      members: [s.client],
      ...spendOf(s),
      conversations: sumDailyInWindow(s.chat?.daily_convs_started ?? s.chat?.daily_convs, win),
      conversationsApprox: !s.chat?.daily_convs_started && Object.keys(s.chat?.daily_convs || {}).length > 0,
      messages: sumDailyInWindow(s.chat?.daily_messages, win),
      activeUsers,
      provisionedUsers: s.users?.provisioned_users || 0,
      costPerConversation: null,
      costPerMessage: null,
      costPerActivePerson: null,
      messagesPerActivePerson: null,
      spendPerActivePerson: null,
      creditsWindow: cr ? sumDailyInWindow(cr.daily_credits, win) : null,
      creditsMonth: cr && (!cr.current_month || cr.current_month === billingMonth) ? cr.current_month_used || 0 : null,
      creditsBalance: cr ? (cr.standing?.available_balance ?? 0) : null,
      schedules: s.schedules?.count || 0,
      activeSchedules: s.schedules?.by_derived_status?.active || 0,
      scheduledRuns: sumDailyInWindow(s.scheduled_runs?.daily_runs, win),
      agents: (s.agents?.agents?.count || 0) + (s.agents?.user_agents?.count || 0),
      knowledgeBases: s.integrations?.knowledge_bases?.count || 0,
      integrations: s.pipedream?.distinct_active_apps || 0,
      integrationConnections: s.pipedream?.active_connections || 0,
      peopleWithIntegrations: s.pipedream?.users_with_apps ?? null,
      dataConnectors: s.integrations?.data_connectors?.count || 0,
      showsCredits: cfg.show_credits ?? null,
      lendsModelQuota: Boolean(cfg.allow_bedrock_quota_sharing),
      borrowsModelQuotaFrom: cfg.bedrock_account || null,
      timeSavedMinutes: savedWindowed
        ? sumDailyInWindow(s.impact?.daily_time_saved_minutes, win)
        : s.impact?.total_time_saved_minutes || 0,
      apps: ua?.apps?.published ?? null,
      appBuilder: cfg.app_builder ?? null,
      appRuns: ua?.daily?.runs ? sumDailyInWindow(ua.daily.runs, win) : null,
      generatedAt: fresh?.generatedAt ?? s.generated_at,
      ageHours: fresh ? fresh.ageHours : ageHours(s.generated_at),
      stale: fresh ? fresh.stale : isStaleAt(s.generated_at),
      failed: fresh?.failed ?? false,
      error: fresh?.error,
      contract: terms?.badge?.label ?? null,
      contractVariant: terms?.badge?.variant ?? null,
      contractHint: terms?.badge?.hint,
      collectible: terms?.collectible ? 'yes' : 'no',
      product: productOf(s),
      inactive: Boolean(inactive?.has(s.client) || terms?.inactive),
    };
  };

  /**
   * A shared AWS account as one row. The money comes from the account summed
   * as a small view: its bill once, less its model lines, plus the model
   * usage of every stack on it. Counts are added up stack by stack. What
   * cannot be added (kind, region, the switches) is the largest stack's.
   */
  const foldAccount = (accountId: string, group: ClientSnapshot[]): StackRow => {
    // An active stack leads, so an Inactive one never stands for an active client's account.
    const parts = group
      .map(rowFor)
      .sort(
        (a, b) => Number(a.inactive) - Number(b.inactive) || b.chatCost - a.chatCost || a.label.localeCompare(b.label)
      );
    const lead = parts[0];
    const account = aggregateSnapshots(group, {
      key: `account:${accountId}`,
      kind: 'account',
      tiers: group[0].portal?.tiers,
      tierPaths: group[0].portal?.tier_paths,
      inactive,
      contractOf,
    });
    const contracts = Array.from(new Set(parts.map((r) => r.contract).filter((c): c is string => Boolean(c))));
    const variants = new Set(parts.map((r) => r.contractVariant));
    const collectible = parts.every((r) => r.collectible === 'yes')
      ? 'yes'
      : parts.some((r) => r.collectible !== 'no')
        ? 'some'
        : 'no';
    const named = friendlyAccountName(accountId);
    const names = parts.map((r) => r.label);
    const label = named.startsWith('Account ')
      ? names.length <= 3
        ? names.join(' + ')
        : `${names.slice(0, 2).join(' + ')} + ${names.length - 2} more`
      : named;
    const oldest = parts.reduce((a, r) => ((r.ageHours ?? -1) > (a.ageHours ?? -1) ? r : a), lead);
    const failed = parts.find((r) => r.failed);
    return {
      ...lead,
      label,
      sharesAccountWith: [],
      members: parts.map((r) => r.client),
      ...spendOf(account),
      conversations: parts.reduce((a, r) => a + r.conversations, 0),
      conversationsApprox: parts.some((r) => r.conversationsApprox),
      messages: parts.reduce((a, r) => a + r.messages, 0),
      activeUsers: sumWhenAllKnown(parts.map((r) => r.activeUsers)),
      provisionedUsers: parts.reduce((a, r) => a + r.provisionedUsers, 0),
      creditsWindow: sumOrNull(parts.map((r) => r.creditsWindow)),
      creditsMonth: sumOrNull(parts.map((r) => r.creditsMonth)),
      creditsBalance: sumOrNull(parts.map((r) => r.creditsBalance)),
      schedules: parts.reduce((a, r) => a + r.schedules, 0),
      activeSchedules: parts.reduce((a, r) => a + r.activeSchedules, 0),
      scheduledRuns: parts.reduce((a, r) => a + r.scheduledRuns, 0),
      agents: parts.reduce((a, r) => a + r.agents, 0),
      knowledgeBases: parts.reduce((a, r) => a + r.knowledgeBases, 0),
      // A tool used in two of the stacks is counted in each: the lists that would say are in another tier.
      integrations: parts.reduce((a, r) => a + r.integrations, 0),
      integrationConnections: parts.reduce((a, r) => a + r.integrationConnections, 0),
      peopleWithIntegrations: sumOrNull(parts.map((r) => r.peopleWithIntegrations)),
      dataConnectors: parts.reduce((a, r) => a + r.dataConnectors, 0),
      timeSavedMinutes: parts.reduce((a, r) => a + r.timeSavedMinutes, 0),
      apps: sumOrNull(parts.map((r) => r.apps)),
      appRuns: sumOrNull(parts.map((r) => r.appRuns)),
      generatedAt: oldest.generatedAt,
      ageHours: oldest.ageHours,
      stale: parts.some((r) => r.stale),
      failed: Boolean(failed),
      error: failed?.error,
      contract: contracts.length ? contracts.join(', ') : null,
      contractVariant: variants.size === 1 ? lead.contractVariant : null,
      contractHint: contracts.length === 1 ? parts.find((r) => r.contract === contracts[0])?.contractHint : undefined,
      collectible,
      product: parts.every((r) => r.product === 'nolia') ? 'nolia' : 'numa',
      inactive: parts.every((r) => r.inactive),
    };
  };

  let rows: StackRow[];
  if (options.onePerAccount) {
    const folded = new Set<string>();
    rows = [];
    for (const s of stacks) {
      const acct = s.client_config?.client_account_id;
      const group = acct && !isInfraAccount(s) ? byAccount.get(acct) || [] : [];
      if (group.length < 2) rows.push(rowFor(s));
      else if (!folded.has(acct!)) {
        folded.add(acct!);
        rows.push(foldAccount(acct!, group));
      }
    }
  } else {
    rows = stacks.map(rowFor);
  }

  // By spend once every row has it. Until then by model usage, which the
  // core data holds for every row: an order that does not shift while the
  // per-service data loads. A fallback built from the bill counted model use
  // twice for most stacks and reshuffled the list the moment the data came in.
  const sortedBy: StackRows['sortedBy'] = rows.every((r) => r.spend !== null) ? 'spend' : 'chatCost';
  rows.sort((a, b) =>
    sortedBy === 'spend'
      ? (b.spend as number) - (a.spend as number) || a.label.localeCompare(b.label)
      : b.chatCost - a.chatCost || a.label.localeCompare(b.label)
  );
  // Worked out last, from the row's own columns, so a folded account's ratios come from its summed figures.
  const per = (top: number | null, bottom: number | null) =>
    top !== null && bottom !== null ? ratio(top, bottom) : null;
  for (const r of rows) {
    r.costPerConversation = per(r.chatCost, r.conversations);
    r.costPerMessage = per(r.chatCost, r.messages);
    r.costPerActivePerson = per(r.chatCost, r.activeUsers);
    r.messagesPerActivePerson = per(r.messages, r.activeUsers);
    r.spendPerActivePerson = per(r.spend, r.activeUsers);
  }
  return { rows, sortedBy, scopes: { timeSaved: savedWindowed ? 'window' : 'all-time' }, pending: Array.from(pending) };
}

export interface Outlier {
  client: string;
  metric: 'spend' | 'chatCost' | 'costPerConversation' | 'costPerMessage';
  value: number;
  /** The mean of the stacks compared. */
  mean: number;
  /** How many standard deviations above the mean. Always more than 2. */
  zScore: number;
}

// A unit cost from a handful of conversations says nothing: leave quiet stacks out of the comparison.
const OUTLIER_MIN_CONVERSATIONS = 20;
const OUTLIER_MIN_MESSAGES = 50;

/**
 * Stacks far above the rest in the window (more than two standard deviations)
 * on spend, on chat cost, or on the cost of one conversation or one message.
 * `spend` and `chatCost` name the largest stacks; the two unit costs name the
 * costly ones whatever their size. Filter on `metric` for the list you want.
 * Infrastructure accounts are not compared, and the unit costs compare only
 * stacks with enough activity for the ratio to mean something. Furthest first.
 */
export function selectOutliers(rows: StackRow[]): Outlier[] {
  const stacks = rows.filter((r) => r.kind !== 'infra');
  const out: Outlier[] = [];
  const check = (metric: Outlier['metric'], pairs: Array<{ client: string; value: number }>) => {
    for (const o of detectOutliers(pairs.map((p) => ({ client: p.client, ce_total: p.value, chat_cost: 0 })))) {
      if (o.metric === 'ce_total')
        out.push({ client: o.client, metric, value: o.value, mean: o.mean, zScore: o.z_score });
    }
  };
  check(
    'spend',
    stacks.map((r) => ({ client: r.client, value: r.spend ?? r.awsBill + r.chatCost }))
  );
  check(
    'chatCost',
    stacks.map((r) => ({ client: r.client, value: r.chatCost }))
  );
  check(
    'costPerConversation',
    stacks
      .filter((r) => r.conversations >= OUTLIER_MIN_CONVERSATIONS)
      .map((r) => ({ client: r.client, value: r.chatCost / r.conversations }))
  );
  check(
    'costPerMessage',
    stacks
      .filter((r) => r.messages >= OUTLIER_MIN_MESSAGES)
      .map((r) => ({ client: r.client, value: r.chatCost / r.messages }))
  );
  return out.sort((a, b) => b.zScore - a.zScore);
}

// ─── per-account rows ────────────────────────────────────────────────────

export interface AccountRow {
  accountId: string;
  /** The AWS account's name, or a short form of its id. */
  label: string;
  /** The stacks or the infrastructure row that sit in the account. */
  members: string[];
  /** The selection id of the row the bill was read from. */
  client: string;
  payerAccountId?: string;
  /** The account's AWS bill in the window. For a standalone customer: the Numa part of it. */
  awsBill: number;
  /** Share of the listed accounts' bill, 0 to 1. */
  share: number;
  /** The bill by service, largest first. Null until the `services` tier is loaded. */
  services: Array<{ service: string; usd: number }> | null;
  generatedAt?: string;
}

export interface AccountRows {
  /** Largest bill first. One row per AWS account, read from its cost owner (`costOwnerPerAccount`). */
  rows: AccountRow[];
  total: number;
  /** `services` when a row's breakdown is still to load. */
  pending: FleetTier[];
}

/** The AWS bill by account for the window. The view the Infrastructure tab is built on. */
export function selectAccountRows(stacks: ClientSnapshot[], win: WindowState): AccountRows {
  const pending = new Set<FleetTier>();
  const members = new Map<string, string[]>();
  for (const s of stacks) {
    const key = s.client_config?.client_account_id || `unknown:${s.client}`;
    members.set(key, [...(members.get(key) || []), s.client]);
  }
  const rows: AccountRow[] = costOwnerPerAccount(stacks).map((s) => {
    const { ce } = ceForSnapshot(s);
    const accountId = s.client_config?.client_account_id || '';
    let services: AccountRow['services'] = null;
    if (ce.by_service_daily) {
      services = Object.entries(ce.by_service_daily)
        .map(([service, days]) => ({ service, usd: sumDailyInWindow(days, win) }))
        .filter((r) => r.usd > 0)
        .sort((a, b) => b.usd - a.usd);
    } else {
      const need = tiersNeededFor(s, 'cost_explorer.by_service_daily');
      if (need.length) need.forEach((t) => pending.add(t));
      else services = [];
    }
    return {
      accountId,
      label: isInfraAccount(s) ? infraDisplayName(s) : friendlyAccountNameOr(accountId, s.client),
      members: members.get(accountId || `unknown:${s.client}`) || [s.client],
      client: s.client,
      payerAccountId: s.client_config?.payer_account_id,
      awsBill: sumDailyInWindow(ce.totals_by_day, win),
      share: 0,
      services,
      generatedAt: s.generated_at,
    };
  });
  const total = rows.reduce((a, r) => a + r.awsBill, 0);
  for (const r of rows) r.share = total > 0 ? r.awsBill / total : 0;
  rows.sort((a, b) => b.awsBill - a.awsBill);
  return { rows, total, pending: Array.from(pending) };
}

export interface ServiceLine {
  service: string;
  usd: number;
  /** Share of the listed bill, 0 to 1. */
  share: number;
}

/**
 * The AWS bill by service across a set of accounts, as billed, largest
 * first. For a selection that is cost only (the infrastructure accounts).
 * `selectServiceMix` cannot stand in there: it swaps the bill's model lines
 * for token-priced chat cost, and an account with no Numa stack has no chat,
 * so its model spend would drop out of the list. Takes the rows of
 * `selectAccountRows`. Null while any account's breakdown is still loading.
 */
export function selectBillByService(rows: AccountRow[]): ServiceLine[] | null {
  const totals = new Map<string, number>();
  for (const row of rows) {
    if (row.services === null) return null;
    for (const s of row.services) totals.set(s.service, (totals.get(s.service) || 0) + s.usd);
  }
  const total = Array.from(totals.values()).reduce((a, b) => a + b, 0);
  return Array.from(totals, ([service, usd]) => ({ service, usd, share: total > 0 ? usd / total : 0 })).sort(
    (a, b) => b.usd - a.usd
  );
}

/** One amount as a share of another, 0 to 1. Null when there is nothing to divide by. */
export const shareOf = (part: number, whole: number): number | null => (whole > 0 ? part / whole : null);

/** A named shared account reads by its name; any other account reads by its one stack. */
function friendlyAccountNameOr(accountId: string, client: string): string {
  const name = friendlyAccountName(accountId);
  return name.startsWith('Account ') || name === 'Unknown account' ? client : name;
}

// ─── conversations ───────────────────────────────────────────────────────

export interface ConversationRow extends TopConversation {
  client: string;
  /** The person's email when the `users` tier is loaded, otherwise a short form of the id. */
  userLabel: string;
  spanSeconds: number | null;
}

export interface TopConversations {
  /**
   * The costliest conversations that were active inside the window, costliest
   * first. Every number on a row is the conversation's lifetime total, not its
   * share of the window: the snapshot keeps each stack's 50 costliest
   * conversations, with no split by day.
   */
  rows: ConversationRow[];
  /** Always `all-time`: see `rows`. */
  scope: Scope;
  /** Conversations on record across the stacks that are not in any stack's list of 50. */
  notListed: number;
  pending: FleetTier[];
}

/** The costliest conversations active in the window. Needs the `conversations` tier. */
export function selectTopConversations(
  stacks: ClientSnapshot[],
  win: WindowState,
  limit = 50
): Tiered<TopConversations> {
  const need = tiersNeededAcross(stacks, 'chat.top_conversations');
  if (need.length) return { needs: need };
  const pending = tiersNeededAcross(stacks, 'users.sub_to_email');
  const rows: ConversationRow[] = [];
  let listed = 0;
  let onRecord = 0;
  for (const s of stacks) {
    const top = s.chat?.top_conversations || [];
    listed += top.length;
    onRecord += (s.chat?.top_conversations_kept ?? top.length) + (s.chat?.top_conversations_dropped ?? 0);
    for (const c of top) {
      if (!topConvInWindow(c, win)) continue;
      const email = c.user_id ? s.users?.sub_to_email?.[c.user_id] : undefined;
      rows.push({
        ...c,
        client: s.client,
        userLabel: email || (c.user_id ? `${c.user_id.slice(0, 8)}…` : ''),
        spanSeconds: topConvSpanSeconds(c),
      });
    }
  }
  rows.sort((a, b) => b.total_cost_usd - a.total_cost_usd);
  return { rows: rows.slice(0, limit), scope: 'all-time', notListed: Math.max(0, onRecord - listed), pending };
}

// ─── the long lists ──────────────────────────────────────────────────────

export interface Inventory {
  /**
   * `appTitle` is set on a schedule that runs an app and not an agent: the
   * app's name when the `apps` tier is loaded, otherwise null (see
   * `pending`). `app_id` on the row says it is an app schedule either way.
   */
  schedules: Array<ScheduleRow & { client: string; appTitle: string | null }>;
  /** `personal` is true for an agent a person built for themselves. */
  agents: Array<AgentRow & { client: string; personal: boolean }>;
  knowledgeBases: Array<KnowledgeBaseRow & { client: string }>;
  /** Outside tools in use, most people first. `stacks` is how many stacks use the tool. */
  integrations: Array<{ app: string; people: number; stacks: number }>;
  /** Tiers that would fill the app names still null. */
  pending: FleetTier[];
}

/** Every schedule, agent, knowledge base and connected tool in the stacks given. Needs the `inventory` tier. */
export function selectInventory(stacks: ClientSnapshot[]): Tiered<Inventory> {
  const need = tiersNeededAcross(
    stacks,
    'schedules.rows',
    'agents.agents.rows',
    'agents.user_agents.rows',
    'integrations.knowledge_bases.rows',
    'pipedream.by_app_active'
  );
  if (need.length) return { needs: need };
  const out: Inventory = { schedules: [], agents: [], knowledgeBases: [], integrations: [], pending: [] };
  const tools = new Map<string, { app: string; people: number; stacks: number }>();
  const pendingApps = new Set<FleetTier>();
  for (const s of stacks) {
    const appTitles = new Map<string, string>();
    for (const app of s.user_apps?.rows || []) if (app.title) appTitles.set(app.id, app.title);
    for (const r of s.schedules?.rows || []) {
      if (r.app_id && !appTitles.has(r.app_id)) tiersNeededFor(s, 'user_apps.rows').forEach((t) => pendingApps.add(t));
      out.schedules.push({ ...r, client: s.client, appTitle: r.app_id ? (appTitles.get(r.app_id) ?? null) : null });
    }
    for (const r of s.agents?.agents?.rows || []) out.agents.push({ ...r, client: s.client, personal: false });
    for (const r of s.agents?.user_agents?.rows || []) out.agents.push({ ...r, client: s.client, personal: true });
    for (const r of s.integrations?.knowledge_bases?.rows || []) out.knowledgeBases.push({ ...r, client: s.client });
    for (const a of s.pipedream?.by_app_active || []) {
      const row = tools.get(a.app_name) || { app: a.app_name, people: 0, stacks: 0 };
      row.people += a.user_count || 0;
      row.stacks += 1;
      tools.set(a.app_name, row);
    }
  }
  out.integrations = Array.from(tools.values()).sort((a, b) => b.people - a.people || a.app.localeCompare(b.app));
  out.pending = Array.from(pendingApps);
  return out;
}

// ─── apps and dashboards ─────────────────────────────────────────────────
//
// In the apps record a missing key means "not available", never zero: when a
// client lacks a source, the rollup leaves that source's keys out. Every
// figure here keeps that. A value of `null` is "not available" and a tile
// shows it as such (`kpiProps` writes "n/a"). A value of 0 is a real zero.

/** The app figures that add up day by day, so a window sum is exact. */
export type AppsWindowKey =
  | 'runs'
  | 'buildRuns'
  | 'automationRuns'
  | 'automationFailures'
  | 'credits'
  | 'costUsd'
  | 'aiRuns'
  | 'publishes'
  | 'newApps'
  | 'deletedApps'
  | 'publicViews'
  | 'submissions'
  | 'buildCostUsd';

const APPS_WINDOW_SOURCE: Record<AppsWindowKey, string> = {
  runs: 'runs',
  buildRuns: 'build_runs',
  automationRuns: 'automation_runs',
  automationFailures: 'automation_failures',
  credits: 'credits',
  costUsd: 'cost_usd',
  aiRuns: 'ai_runs',
  publishes: 'publishes',
  newApps: 'new_apps',
  deletedApps: 'deleted_apps',
  publicViews: 'public_views',
  submissions: 'submissions',
  buildCostUsd: 'build_cost_usd',
};

const NOT_AVAILABLE = 'Not available: this client has no record of it.';

/** The window figures counted from run records. The run history starts the night the record began, so only these can be short. */
const RUN_RECORD_FIGURES: ReadonlySet<AppsWindowKey> = new Set<AppsWindowKey>(['runs', 'buildRuns']);

export interface AppsFigures {
  /**
   * How many apps and dashboards exist, as of the last nightly update. Scope
   * `now`. Every key is optional: one that is absent is not available, so
   * show "n/a" for it, never 0. `total`, `apps`, `dashboards`, `published`,
   * `draft_only`, `statics`, `with_public_link` and `distinct_owners` count
   * every app record. The other keys count published apps only.
   */
  inventory: UserAppsInventory;
  /** Records and files the apps hold. Scope `now`. A null field is not available. Null when the snapshot says nothing. */
  storage: {
    scopes: number | null;
    sharedScopes: number | null;
    privateScopes: number | null;
    draftScopes: number | null;
    items: number | null;
    bytes: number | null;
    files: number | null;
  } | null;
  /** Which sources the record was read from. A false source means its figures are not available. Null on an older snapshot. */
  sources: UserAppsBlock['sources'] | null;
  /** The day the apps numbers run up to. */
  asOf?: string;
  /**
   * False when the snapshot holds the inventory and nothing else: an older
   * snapshot keeps no run history. `window`, the three `last*` blocks,
   * `appsRun` and `people` are then null. Show the inventory and say the
   * history has not started.
   */
  hasHistory: boolean;
  /**
   * The first day from which run counts are complete. On a view: the day the
   * EARLIEST stack's counts start. A window that starts before it is only
   * partly covered.
   */
  historyFrom?: string;
  /**
   * On a view: the first day from which EVERY stack's run counts are
   * complete (the latest start among them). Say "complete from" this day. On
   * one stack it is the same day as `historyFrom`.
   */
  historyCompleteFrom?: string;
  /** On a view: the last day counted for the stack furthest behind. `asOf` is the newest. */
  asOfBehind?: string;
  /**
   * Sums inside the window. A figure whose value is null is not available.
   * `runs` are runs of a published version by a person or an entry point.
   * `buildRuns` are draft and test runs, and a floor (test runs expire).
   * Only `runs` and `buildRuns` come from run records, so only they are
   * marked approximate when the window starts before the run history is
   * complete. Credits come from the ledger, and new apps and publishes from
   * the app records: each has its own dates and its full history.
   */
  window: Record<AppsWindowKey, Figure<number | null>> | null;
  /** Totals for the 7, 30 and 90 days before the last nightly update, as the rollup counted them. An absent key is not available. */
  last7: UserAppsTotals | null;
  last30: UserAppsTotals | null;
  last90: UserAppsTotals | null;
  /** Apps with one run or more in the window, each counted once. Needs the `apps` tier. Value null: not available. */
  appsRun: Tiered<Figure<number | null>> | null;
  /** People who started an app run in the window, each counted once. Needs the `apps` tier. Value null: not available. */
  people: Tiered<Figure<number | null>> | null;
  /** Stacks in a summed view whose snapshot keeps no run history yet. They are left out of the history figures. */
  stacksWithoutHistory: string[];
}

const orNull = (n: number | undefined): number | null => (n === undefined ? null : n);

/** Apps and dashboards for the selection, or null when no stack in it has an apps record. */
export function selectApps(data: DashboardView, win: WindowState): AppsFigures | null {
  const ua = data.user_apps;
  if (!ua?.available || !ua.apps) return null;
  const d = ua.data;
  const storage = d
    ? {
        scopes: orNull(d.scopes),
        sharedScopes: orNull(d.shared_scopes),
        privateScopes: orNull(d.private_scopes),
        draftScopes: orNull(d.draft_scopes),
        items: orNull(d.items),
        bytes: orNull(d.bytes),
        files: orNull(d.files),
      }
    : null;
  const without = data.portal?.missing?.['user_apps.daily'] ?? [];
  if (!ua.daily) {
    return {
      inventory: ua.apps,
      storage,
      sources: ua.sources ?? null,
      asOf: ua.as_of,
      hasHistory: false,
      window: null,
      last7: null,
      last30: null,
      last90: null,
      appsRun: null,
      people: null,
      stacksWithoutHistory: without,
    };
  }

  const from = ua.history_from;
  // A view is complete only from the day its latest stack starts.
  const completeFrom = ua.history_complete_from ?? from;
  let quality: { approx?: boolean; note?: string } = {};
  if (completeFrom && win.startDate < completeFrom) {
    quality = {
      approx: true,
      note:
        completeFrom === from
          ? `App run counts are complete from ${completeFrom}. Earlier days in the window are not counted.`
          : `App run counts start on ${from} for the first stack and are complete for every stack from ${completeFrom}. Earlier days in the window are short.`,
    };
  } else if (without.length) {
    quality = {
      approx: true,
      note: `${without.length === 1 ? '1 stack keeps' : `${without.length} stacks keep`} no run history yet and ${without.length === 1 ? 'is' : 'are'} left out: ${nameList(without)}.`,
    };
  }
  const daily = ua.daily as Record<string, Record<string, number> | undefined>;
  const windowFigures = {} as Record<AppsWindowKey, Figure<number | null>>;
  for (const key of Object.keys(APPS_WINDOW_SOURCE) as AppsWindowKey[]) {
    const map = daily[APPS_WINDOW_SOURCE[key]];
    windowFigures[key] =
      map === undefined
        ? fig<number | null>(null, 'window', { note: NOT_AVAILABLE })
        : fig<number | null>(sumDailyInWindow(map, win), 'window', RUN_RECORD_FIGURES.has(key) ? quality : undefined);
  }

  let appsRun: Tiered<Figure<number | null>>;
  let people: Tiered<Figure<number | null>>;
  const needApps = tiersNeededFor(data, 'user_apps.daily_by_app', 'user_apps.users');
  if (needApps.length) {
    appsRun = { needs: needApps };
    people = { needs: needApps };
  } else {
    const runsByApp = ua.daily_by_app?.runs;
    appsRun = runsByApp
      ? fig<number | null>(
          Object.values(sumByKeyInWindow(runsByApp, win)).filter((n) => n > 0).length,
          'window',
          quality
        )
      : fig<number | null>(null, 'window', { note: NOT_AVAILABLE });

    if (!ua.users) {
      people = fig<number | null>(null, 'window', { note: NOT_AVAILABLE });
    } else {
      let n = 0;
      let exact = true;
      for (const u of Object.values(ua.users)) {
        if (u.daily) {
          // The person's runs by day: in the window when any day of it has one.
          if (
            Object.entries(u.daily).some(([day, runs]) => day >= win.startDate && day <= win.endDate && (runs || 0) > 0)
          )
            n++;
        } else {
          // Only a first and a last day on record. Somebody active before and
          // after the window, and not inside it, is counted: the figure can
          // read high, never low.
          exact = false;
          if (u.first_day && u.last_day && u.first_day <= win.endDate && u.last_day >= win.startDate) n++;
        }
      }
      people = fig<number | null>(
        n,
        'window',
        exact
          ? quality
          : {
              approx: true,
              note: 'People whose first and last app use are on either side of the window are counted, so this can read high.',
            }
      );
    }
  }

  return {
    inventory: ua.apps,
    storage,
    sources: ua.sources ?? null,
    asOf: ua.as_of,
    hasHistory: true,
    historyFrom: from,
    historyCompleteFrom: completeFrom,
    asOfBehind: ua.as_of_behind ?? ua.as_of,
    window: windowFigures,
    last7: ua.totals?.d7 ?? null,
    last30: ua.totals?.d30 ?? null,
    last90: ua.totals?.d90 ?? null,
    appsRun,
    people,
    stacksWithoutHistory: without,
  };
}

export interface AppRow extends UserAppRow {
  client: string;
  /** The owner's email when the `users` tier is loaded, otherwise a short form of the id. */
  ownerLabel: string;
  /** Runs and credits inside the window. Null: not available (the snapshot has no per-app history for it). */
  runsInWindow: number | null;
  creditsInWindow: number | null;
  /** People who started a run of the app in the window. Null: not available. */
  peopleInWindow: number | null;
}

export interface AppRows {
  /** Most runs in the window first, then most runs on record. */
  rows: AppRow[];
  /** `window` when `runsInWindow` is filled for every row; otherwise sort and show `runs` and tag it `all-time`. */
  scope: Scope;
  pending: FleetTier[];
}

/** One row per app or dashboard. Needs the `apps` tier. */
export function selectAppRows(stacks: ClientSnapshot[], win: WindowState): Tiered<AppRows> {
  const need = tiersNeededAcross(stacks, 'user_apps.rows', 'user_apps.daily_by_app');
  if (need.length) return { needs: need };
  const pending = tiersNeededAcross(stacks, 'users.sub_to_email');
  const rows: AppRow[] = [];
  let windowed = true;
  for (const s of stacks) {
    const ua = s.user_apps;
    if (!ua?.rows) continue;
    const runs = ua.daily_by_app?.runs ? sumByKeyInWindow(ua.daily_by_app.runs, win) : undefined;
    const credits = ua.daily_by_app?.credits ? sumByKeyInWindow(ua.daily_by_app.credits, win) : undefined;
    if (!runs) windowed = false;
    // A person's last day on each app: in the window when that day is, or
    // later. "Later" can count somebody whose only runs were after the window
    // started and before it, so this is exact for a window that ends today
    // and can read high for an older one.
    let people: Record<string, number> | undefined;
    for (const u of Object.values(ua.users || {})) {
      if (!u.last_day_by_app) continue;
      people ||= {};
      for (const [appId, lastDay] of Object.entries(u.last_day_by_app)) {
        if (lastDay >= win.startDate) people[appId] = (people[appId] || 0) + 1;
      }
    }
    for (const r of ua.rows) {
      const email = r.owner ? s.users?.sub_to_email?.[r.owner] : undefined;
      rows.push({
        ...r,
        client: s.client,
        ownerLabel: email || (r.owner ? `${r.owner.slice(0, 8)}…` : ''),
        runsInWindow: runs ? runs[r.id] || 0 : null,
        creditsInWindow: credits ? credits[r.id] || 0 : null,
        peopleInWindow: people ? people[r.id] || 0 : null,
      });
    }
  }
  rows.sort((a, b) => (b.runsInWindow ?? 0) - (a.runsInWindow ?? 0) || (b.runs ?? 0) - (a.runs ?? 0));
  return { rows, scope: windowed && rows.length > 0 ? 'window' : 'all-time', pending };
}

export interface AppsStackRow {
  client: string;
  label: string;
  kind: StackKind;
  /** Inventory, as of the last nightly update. Null: the snapshot does not say. */
  total: number | null;
  apps: number | null;
  dashboards: number | null;
  published: number | null;
  /** People who own an app or dashboard. */
  builders: number | null;
  /** In the window. Null: not available (no run history, or the client has no record of it). */
  runs: number | null;
  credits: number | null;
  /** People who started an app run in the window. Null until the `apps` tier is loaded, or when not available. */
  people: number | null;
  /** False when the stack's snapshot keeps no run history yet. */
  hasHistory: boolean;
}

export interface AppsByStack {
  /** Stacks that hold one app or dashboard or more. Most runs in the window first, then most apps. */
  rows: AppsStackRow[];
  /** Tiers that would fill the `people` cells still null. */
  pending: FleetTier[];
}

/** Apps and dashboards stack by stack, for a fleet-wide view. Core data, plus the `apps` tier for `people`. */
export function selectAppsByStack(stacks: ClientSnapshot[], win: WindowState): AppsByStack {
  const pending = new Set<FleetTier>();
  const rows: AppsStackRow[] = [];
  for (const s of stacks) {
    const ua = s.user_apps;
    if (!ua?.available || !ua.apps || !(ua.apps.total > 0)) continue;
    let people: number | null = null;
    if (ua.daily) {
      const need = tiersNeededFor(s, 'user_apps.users');
      if (need.length) need.forEach((t) => pending.add(t));
      else if (ua.users) {
        people = 0;
        for (const u of Object.values(ua.users)) {
          const inWindow = u.daily
            ? Object.entries(u.daily).some(([d, n]) => d >= win.startDate && d <= win.endDate && (n || 0) > 0)
            : Boolean(u.first_day && u.last_day && u.first_day <= win.endDate && u.last_day >= win.startDate);
          if (inWindow) people++;
        }
      }
    }
    rows.push({
      client: s.client,
      label: stackLabel(s),
      kind: stackKind(s),
      total: orNull(ua.apps.total),
      apps: orNull(ua.apps.apps),
      dashboards: orNull(ua.apps.dashboards),
      published: orNull(ua.apps.published),
      builders: orNull(ua.apps.distinct_owners),
      runs: ua.daily?.runs ? sumDailyInWindow(ua.daily.runs, win) : null,
      credits: ua.daily?.credits ? sumDailyInWindow(ua.daily.credits, win) : null,
      people,
      hasHistory: Boolean(ua.daily),
    });
  }
  rows.sort((a, b) => (b.runs ?? -1) - (a.runs ?? -1) || (b.total ?? 0) - (a.total ?? 0));
  return { rows, pending: Array.from(pending) };
}

// ─── series for charts ───────────────────────────────────────────────────

export type SeriesKey =
  | 'spend'
  | 'awsBill'
  | 'chatCost'
  | 'conversations'
  | 'messages'
  | 'toolCalls'
  | 'turns'
  | 'peopleActive'
  | 'scheduledRuns'
  | 'scheduledCost'
  | 'failedRuns'
  | 'credits'
  | 'requests'
  | 'errors'
  | 'timeSavedMinutes'
  | 'appRuns'
  | 'appPeople'
  | 'appCredits';

export interface Series {
  /** Every day of the window, oldest first. */
  days: string[];
  /** One value per day. A day with nothing is 0. */
  values: number[];
  /** False when the snapshot does not carry this series: draw an empty state, not a flat line. */
  available: boolean;
  approx?: boolean;
  note?: string;
}

/**
 * One daily series, ready for a chart. `days` runs from the window's first
 * day to the last day the data holds (the day of the nightly update), never
 * past it: the days after that have no data yet, and a point at 0 there
 * would draw use falling off a cliff. `peopleActive` and `appPeople` are
 * distinct people per day: do not add the days up.
 */
export function selectSeries(data: DashboardView, win: WindowState, key: SeriesKey): Tiered<Series> {
  const from = (map: Record<string, number> | undefined, extra?: { approx?: boolean; note?: string }): Series => {
    const days = chartDays(data, win, map);
    return { days, values: days.map((d) => map?.[d] || 0), available: map !== undefined, ...extra };
  };
  const chat = data.chat;
  switch (key) {
    case 'spend': {
      const spend = selectSpend(data, win);
      if (!isReady(spend)) return spend;
      return {
        days: spend.daily.days,
        values: spend.daily.inferred,
        available: true,
        approx: spend.inferred.approx,
        note: spend.inferred.note,
      };
    }
    case 'awsBill':
      return from(data.cost_explorer?.totals_by_day);
    case 'chatCost':
      // Model spend: a summed view adds the Inactive stacks it hides, as every spend figure does.
      return from(chat?.daily_cost === undefined ? undefined : spendChatDaily(data));
    case 'conversations': {
      const { map, quality } = conversationSeries(data);
      return from(map, quality);
    }
    case 'messages':
      return from(chat?.daily_messages);
    case 'toolCalls':
      return from(chat?.daily_tool_calls);
    case 'turns':
      return from(chat?.daily_turns);
    case 'peopleActive': {
      const { map, exact } = dailyActivePeople(chat);
      return from(map, exact ? undefined : { approx: true, note: SCHEDULE_COUNTS_AS_ACTIVE });
    }
    case 'scheduledRuns':
      return from(data.scheduled_runs?.daily_runs);
    case 'scheduledCost':
      return from(data.scheduled_runs?.daily_cost);
    case 'failedRuns':
      return from(data.scheduled_runs?.daily_failed_runs, leftOut(data, 'scheduled_runs.daily_failed_runs'));
    case 'credits':
      return from(isAggregate(data) ? data.credits_rollup?.daily_credits : data.credits?.daily_credits);
    case 'requests':
      return from(chat?.daily_requests, leftOut(data, 'chat.daily_requests'));
    case 'errors':
      return from(chat?.daily_errors, leftOut(data, 'chat.daily_errors'));
    case 'timeSavedMinutes':
      return from(data.impact?.daily_time_saved_minutes, leftOut(data, 'impact.daily_time_saved_minutes'));
    case 'appRuns':
      return from(data.user_apps?.daily?.runs);
    case 'appPeople':
      return from(data.user_apps?.daily?.users);
    case 'appCredits':
      return from(data.user_apps?.daily?.credits);
  }
}

/**
 * The mean of the `span` days up to and including each day of a series. It
 * reads the days before the window's first day too, so the first days of the
 * line are a full average and not a mean of what the window holds so far.
 * Same days and same `Tiered` answer as `selectSeries` for the key.
 */
export function selectTrailingAverage(data: DashboardView, win: WindowState, key: SeriesKey, span = 7): Tiered<Series> {
  const withLeadIn: WindowState = { ...win, startDate: addDays(win.startDate, -(span - 1)) };
  const series = selectSeries(data, withLeadIn, key);
  if (!isReady(series)) return series;
  const first = series.days.findIndex((d) => d >= win.startDate);
  const from = first < 0 ? series.days.length : first;
  // A mean is a sum over a count of days: the shared trailing sum with one on the bottom for each day.
  const means = trailingRatio(
    series.values,
    series.values.map(() => 1),
    span
  );
  return {
    days: series.days.slice(from),
    values: means.slice(from).map((m) => m ?? 0),
    available: series.available,
    approx: series.approx,
    note: series.note,
  };
}
